/* eslint-disable  */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CoverImageUpload } from '../../container/admin/subscriptions/components/shared/SharedComponents';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Page from '../../components/Page';
import DeleteAlert from '../../components/modal/DeleteModal';
import adminServices from '../../services/adminServices';

import {
  IC,
  SUBJECTS, CLASS_LEVELS, LANGUAGES, BOARDS,
  TEXT_COLORS, HIGHLIGHT_COLORS,
  PREVIEW_CSS,
  CALLOUT_META,
  processCustomBlocks,
  TOOLBAR_GROUPS,
} from './EBookCreatorTemplates';

// ─── Ico component ─────────────────────────────────────────────────────────────
function Ico({ d, size, cls }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size || 16}
      height={size || 16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cls || ''}
    >
      <path d={d} />
    </svg>
  );
}
Ico.propTypes = { d: PropTypes.string.isRequired, size: PropTypes.number, cls: PropTypes.string };
Ico.defaultProps = { size: 16, cls: '' };

// ─── Shared field helpers ──────────────────────────────────────────────────────
const INPUT_CLS = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white';

// Chapter-import limits — keep in step with the whole-eBook importer in EBooksList.
const MAX_DOC_MB = 50;
const DOCX_MIME  = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
// Documents under this size POST straight to the API; larger ones go via a
// presigned S3 URL first (the API gateway caps request bodies at ~10 MB).
const DIRECT_UPLOAD_LIMIT = 9 * 1024 * 1024;

function FieldRow({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
      {children}
    </div>
  );
}
FieldRow.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

// Borderless ring with one coloured edge — Tailwind's animate-spin does the rest,
// so this needs no icon and no library.
function Spinner({ size = 12, cls = 'border-indigo-200 border-t-indigo-600' }) {
  return (
    <span
      aria-hidden
      className={`inline-block rounded-full border-2 animate-spin flex-shrink-0 ${cls}`}
      style={{ width: size, height: size }}
    />
  );
}
Spinner.propTypes = { size: PropTypes.number, cls: PropTypes.string };
Spinner.defaultProps = { size: 12, cls: 'border-indigo-200 border-t-indigo-600' };

function PanelWrapper({ title, icon, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      {/* eBooks -> Edit eBook -> Button: at 1360x768 the header close icon sat under the MUI AppBar, which is fixed at z-index 1100 while this overlay was z-50. */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 shrink-0">
            <Ico d={icon} size={16} />
          </div>
          <h3 className="text-base font-bold text-slate-800">{title}</h3>
          <button type="button" onClick={onClose} className="ml-auto shrink-0 text-slate-400 hover:text-slate-700">
            <Ico d={IC.x} size={18} />
          </button>
        </div>
        <div className="p-5 flex flex-col gap-4 overflow-y-auto flex-1 min-h-0">{children}</div>
      </div>
    </div>
  );
}
PanelWrapper.propTypes = {
  title: PropTypes.string.isRequired, icon: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired, children: PropTypes.node.isRequired,
};

function PanelFooter({ onClose, onInsert, disabled }) {
  return (
    <div className="flex gap-3 pt-2 border-t border-slate-100">
      <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
      <button type="button" onClick={onInsert} disabled={disabled} className="flex-1 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Insert</button>
    </div>
  );
}
PanelFooter.propTypes = { onClose: PropTypes.func.isRequired, onInsert: PropTypes.func.isRequired, disabled: PropTypes.bool };
PanelFooter.defaultProps = { disabled: false };

// ─── Portal Menu ───────────────────────────────────────────────────────────────
function usePortalPos(btnRef, isOpen) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!isOpen || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom, left: r.left });
  }, [isOpen, btnRef]);
  return pos;
}

function PortalMenu({ btnRef, isOpen, children }) {
  const pos = usePortalPos(btnRef, isOpen);
  if (!isOpen) return null;
  return createPortal(
    <div
      data-portal-dropdown
      style={{ position: 'fixed', top: pos.top + 4, left: pos.left, zIndex: 9999 }}
    >
      {children}
    </div>,
    document.body
  );
}
PortalMenu.propTypes = {
  btnRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

// ─── Toolbar ───────────────────────────────────────────────────────────────────
function Toolbar({ textareaRef, value, onChange, onInsertSpecial }) {
  const [openGroup, setOpenGroup] = useState(null);
  const [colorMode, setColorMode] = useState(null);
  const dropRef  = useRef(null);
  const btnRefs  = useRef({});

  useEffect(() => {
    const handler = (e) => {
      const inPortal = e.target.closest('[data-portal-dropdown]');
      if (!inPortal && dropRef.current && !dropRef.current.contains(e.target)) {
        setOpenGroup(null);
        setColorMode(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const insert = useCallback((action) => {
    const el    = textareaRef.current;
    const start = el ? el.selectionStart : value.length;
    const end   = el ? el.selectionEnd   : value.length;
    const sel   = value.slice(start, end);
    let newVal;
    let cursor;

    if (action.insert) {
      newVal = `${value.slice(0, start)}${action.insert}${value.slice(end)}`;
      cursor = start + action.insert.length;
    } else if (action.block) {
      const ls   = value.lastIndexOf('\n', start - 1) + 1;
      const line = value.slice(ls, end || start);
      newVal = `${value.slice(0, ls)}${action.wrap}${line}${value.slice(end || start)}`;
      cursor = ls + action.wrap.length + line.length;
    } else {
      const word = sel || 'text';
      newVal = `${value.slice(0, start)}${action.wrap}${word}${action.wrap}${value.slice(end)}`;
      cursor = start + action.wrap.length + word.length + action.wrap.length;
    }
    onChange(newVal);
    if (el) setTimeout(() => { el.focus(); el.setSelectionRange(cursor, cursor); }, 0);
    setOpenGroup(null);
  }, [value, onChange, textareaRef]);

  const insertColor = useCallback((col, mode) => {
    const el    = textareaRef.current;
    const start = el ? el.selectionStart : value.length;
    const end   = el ? el.selectionEnd   : value.length;
    const sel   = value.slice(start, end) || 'selected text';
    // Colour is an inline style, so it is applied one line at a time and never
    // wraps structure. A selection dragged across a line break or a bullet used
    // to swallow the newline and the "- " marker, and a selection that clipped a
    // bold marker interleaved the two — "*:::color[…]*x:::**". Both produce
    // markdown that cannot be parsed, and the colour was lost along with it.
    // Indentation, list markers and emphasis markers all stay outside the block.
    const wrapLine = (line) => {
      const pre    = (line.match(/^\s*(?:(?:[-*+]|\d+\.)\s+)?/) || [''])[0];
      const rest   = line.slice(pre.length);
      const tailWs = (rest.match(/\s*$/) || [''])[0];
      const text   = rest.slice(0, rest.length - tailWs.length);
      if (!text) return line;
      const lead  = (text.match(/^[*_~`]*/) || [''])[0];
      const trail = (text.match(/[*_~`]*$/) || [''])[0];
      // Markers are lifted out only when they are a matching pair wrapping the
      // whole selection. When they are not — "**Incomplete:** Single opening…" —
      // the pair belongs to text inside the selection, and moving one half of it
      // out would leave the bold unbalanced and print the asterisks.
      const paired = lead.length > 0 && lead === trail && text.length > lead.length + trail.length;
      const core   = paired ? text.slice(lead.length, text.length - trail.length) : text;
      if (!core) return line;
      const block = mode === 'text'
        ? `:::color[${col}]${core}:::`
        : `:::highlight[${col}]${core}:::`;
      return pre + (paired ? lead : '') + block + (paired ? trail : '') + tailWs;
    };
    const snippet = col ? sel.split('\n').map(wrapLine).join('\n') : sel;
    const nv = `${value.slice(0, start)}${snippet}${value.slice(end)}`;
    onChange(nv);
    setColorMode(null);
    setOpenGroup(null);
    if (el) setTimeout(() => { el.focus(); el.setSelectionRange(start + snippet.length, start + snippet.length); }, 0);
  }, [value, onChange, textareaRef]);

  const groupIcons = {
    format: IC.text, blocks: IC.list, math: IC.math, chemistry: IC.chemistry,
    physics: IC.physics, biology: IC.biology, lifecycle: IC.lifecycle,
    general: IC.general, exam: IC.exam, cs: IC.computer,
  };
  const groupColors = {
    format: 'text-slate-600', blocks: 'text-blue-600', math: 'text-indigo-600',
    chemistry: 'text-orange-600', physics: 'text-yellow-600', biology: 'text-green-600',
    lifecycle: 'text-teal-600', general: 'text-purple-600', exam: 'text-red-600', cs: 'text-cyan-600',
  };

  return (
    <div
      ref={dropRef}
      className="flex items-center gap-0.5 border-b border-slate-200 bg-white px-2 overflow-x-auto flex-shrink-0"
      style={{ minHeight: 44 }}
    >
      {TOOLBAR_GROUPS.map((group) => {
        const isOpen = openGroup === group.key;
        const icPath = groupIcons[group.key] || IC.book;
        const colCls = groupColors[group.key] || 'text-slate-600';
        return (
          <div key={group.key} className="flex-shrink-0">
            <button
              ref={(el) => { btnRefs.current[group.key] = el; }}
              type="button"
              title={group.label}
              onClick={() => { setOpenGroup(isOpen ? null : group.key); setColorMode(null); }}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${isOpen ? 'bg-indigo-50 text-indigo-700' : `${colCls} hover:bg-slate-50`}`}
            >
              <Ico d={icPath} size={14} />
              <span className="hidden lg:inline">{group.label}</span>
              <Ico d={IC.chevD} size={10} />
            </button>

            <PortalMenu btnRef={{ current: btnRefs.current[group.key] }} isOpen={isOpen}>
              <div className="bg-white rounded-xl border border-slate-200 shadow-xl min-w-56 max-w-72 py-1 max-h-80 overflow-y-auto">
                <div className="px-3 py-1.5 border-b border-slate-100">
                  <span className={`text-xs font-bold uppercase tracking-wider ${colCls}`}>{group.label}</span>
                </div>
                {group.items.map((act) => (
                  <button
                    key={act.label}
                    type="button"
                    title={act.title}
                    onClick={() => insert(act)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors text-left"
                  >
                    {act.icon && <Ico d={act.icon} size={12} cls="flex-shrink-0 text-slate-400" />}
                    <span className="flex-1 font-medium">{act.label}</span>
                    <span className="text-slate-300 text-xs truncate max-w-24">{act.title}</span>
                  </button>
                ))}
              </div>
            </PortalMenu>
          </div>
        );
      })}

      <div className="w-px h-5 bg-slate-200 mx-1 flex-shrink-0" />

      {/* Text Color */}
      <div className="flex-shrink-0">
        <button
          ref={(el) => { btnRefs.current['color'] = el; }}
          type="button"
          title="Text Color"
          onClick={() => { setColorMode(colorMode === 'text' ? null : 'text'); setOpenGroup(null); }}
          className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold transition-colors ${colorMode === 'text' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
        >
          <Ico d={IC.color} size={14} />
          <span className="hidden lg:inline">Color</span>
        </button>

        <PortalMenu btnRef={{ current: btnRefs.current['color'] }} isOpen={colorMode === 'text'}>
          <div className="bg-white rounded-xl border border-slate-200 shadow-xl p-3 min-w-48">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Text Color</p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {TEXT_COLORS.map((tc) => (
                <button
                  key={tc.label}
                  type="button"
                  title={tc.label}
                  onClick={() => insertColor(tc.color, 'text')}
                  className="w-7 h-7 rounded-full border-2 border-slate-200 hover:scale-110 transition-transform flex items-center justify-center"
                  style={{ background: tc.color || '#f1f5f9', color: tc.color || '#0f172a' }}
                >
                  {!tc.color && <span className="text-xs font-bold">A</span>}
                </button>
              ))}
            </div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Highlight</p>
            <div className="flex flex-wrap gap-1.5">
              {HIGHLIGHT_COLORS.map((hc) => (
                <button
                  key={hc.label}
                  type="button"
                  title={hc.label}
                  onClick={() => insertColor(hc.color, 'highlight')}
                  className="w-7 h-7 rounded border-2 border-slate-200 hover:scale-110 transition-transform"
                  style={{ background: hc.color || '#f1f5f9' }}
                />
              ))}
            </div>
          </div>
        </PortalMenu>
      </div>

      <div className="w-px h-5 bg-slate-200 mx-1 flex-shrink-0" />

      <button type="button" title="Insert Image" onClick={() => { onInsertSpecial('image'); setOpenGroup(null); }} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors flex-shrink-0">
        <Ico d={IC.image} size={14} /><span className="hidden lg:inline">Image</span>
      </button>
      <button type="button" title="Insert Video" onClick={() => { onInsertSpecial('video'); setOpenGroup(null); }} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-rose-50 hover:text-rose-700 transition-colors flex-shrink-0">
        <Ico d={IC.video} size={14} /><span className="hidden lg:inline">Video</span>
      </button>
      <button type="button" title="Insert Button" onClick={() => { onInsertSpecial('button'); setOpenGroup(null); }} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-amber-50 hover:text-amber-700 transition-colors flex-shrink-0">
        <Ico d={IC.btn} size={14} /><span className="hidden lg:inline">Button</span>
      </button>
    </div>
  );
}
Toolbar.propTypes = {
  textareaRef:     PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  value:           PropTypes.string.isRequired,
  onChange:        PropTypes.func.isRequired,
  onInsertSpecial: PropTypes.func.isRequired,
};

// ─── Media addressing ──────────────────────────────────────────────────────────
// Everything in the preview an author can select. Embeds are matched by their
// wrapper, not the <iframe>: pointer events never reach the parent document
// from inside an iframe, so the wrapper's rim is the only part that can be hit.
const MEDIA_SELECTOR = 'img, video, .ebook-video-wrap';

const FIGURE_CLASS = 'ebk-fig';


const MEDIA_EXT = {
  jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image', bmp: 'image', svg: 'image',
  mp4: 'video', webm: 'video', ogv: 'video', mov: 'video',
};

// 'image' | 'video' | null for anything this editor cannot embed.
function mediaKindOfFile(file) {
  const type = String(file?.type || '');
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  const ext = (String(file?.name || '').match(/\.([^.]+)$/) || [, ''])[1].toLowerCase();
  return MEDIA_EXT[ext] || null;
}

const escapeRe = (v) => String(v).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function resizeTargetOf(el) {
  // For an embed the element that carries the width is the iframe, while the
  // element that can be hovered is its wrapper.
  if (el && el.classList && el.classList.contains('ebook-video-wrap')) {
    return el.querySelector('iframe') || el;
  }
  return el;
}

function srcOf(el) {
  if (!el) return '';
  return el.getAttribute('src') || el.src || el.currentSrc || '';
}

function kindOf(el) {
  if (!el) return 'image';
  const tag = el.tagName.toLowerCase();
  if (tag === 'iframe') return 'embed';
  if (tag === 'video')  return 'video';
  return 'image';
}


function mediaPeers(root, kind, src) {
  if (!root) return [];
  return Array.from(root.querySelectorAll('img, video, iframe'))
    .filter((n) => kindOf(n) === kind && srcOf(n) === src);
}


function rectOf(el) {
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

const sameRect = (a, b) => !!a && !!b
  && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;

function alignOf(el) {
  const fig = el && el.closest ? el.closest('div[style*="text-align"]') : null;
  const m = fig && /text-align:\s*(left|center|right)/.exec(fig.getAttribute('style') || '');
  return m ? m[1] : 'center';
}


function widthPctOf(el, contentEl) {
  const style = el.getAttribute('style') || '';
  const m = /max-width:\s*([\d.]+)%/.exec(style) || /(?:^|[^-\w])width:\s*([\d.]+)%/.exec(style);
  if (m) return Math.round(parseFloat(m[1]));
  const cw = contentEl ? contentEl.clientWidth : 0;
  return cw ? Math.min(100, Math.round((el.offsetWidth / cw) * 100)) : 100;
}

function describeMedia(root, el, contentEl) {
  const target = resizeTargetOf(el);
  if (!target) return null;
  const kind = kindOf(target);
  const src  = srcOf(target);
  return {
    kind,
    src,
    index: Math.max(0, mediaPeers(root, kind, src).indexOf(target)),
    alt:   target.getAttribute('alt') || '',
    align: alignOf(target),
    width: widthPctOf(target, contentEl),
  };
}

const MEDIA_CHROME = 'border-radius:.75rem;box-shadow:0 4px 20px rgba(0,0,0,.1)';

const figureOf = (tag, src, alt, pct, align) => {
  const inner = tag === 'video'
    ? `<video src="${src}" controls style="max-width:${pct}%;${MEDIA_CHROME}"></video>`
    : `<img src="${src}" alt="${alt}" style="max-width:${pct}%;${MEDIA_CHROME}" />`;
  return `\n<div class="${FIGURE_CLASS}" style="text-align:${align};margin:1.25rem 0">${inner}</div>\n`;
};

const mediaFigure = (src, alt = 'Image', pct = 100, align = 'center') => figureOf('img', src, alt, pct, align);
const videoFigure = (src, pct = 100, align = 'center') => figureOf('video', src, '', pct, align);

function setStyleProp(tagText, prop, value) {
  const m = /style="([^"]*)"/.exec(tagText);
  if (!m) return tagText.replace(/\s*\/?>\s*$/, (end) => ` style="${prop}:${value}"${end}`);
  const decls = m[1].split(';').map((s) => s.trim()).filter(Boolean);
  const idx   = decls.findIndex((d) => d.slice(0, d.indexOf(':')).trim() === prop);
  if (idx >= 0) decls[idx] = `${prop}:${value}`;
  else decls.push(`${prop}:${value}`);
  return tagText.replace(/style="[^"]*"/, () => `style="${decls.join(';')}"`);
}


const escapeAttr = (v) => String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function setAttr(tagText, name, value) {
  const safe = escapeAttr(value);
  const re   = new RegExp(`\\s${name}="[^"]*"`);
  if (re.test(tagText)) return tagText.replace(re, () => ` ${name}="${safe}"`);
  return tagText.replace(/\s*\/?>\s*$/, (end) => ` ${name}="${safe}"${end}`);
}


function findMediaTag(md, { kind, src, index }) {
  const tag    = kind === 'video' ? 'video' : 'img';
  const re     = new RegExp(`<${tag}\\b(?:[^>"']|"[^"]*"|'[^']*')*>`, 'g');
  const needle = `src="${src}"`;
  let m;
  let seen = 0;
  while ((m = re.exec(md)) !== null) {
    if (m[0].indexOf(needle) === -1) continue;
    if (seen === index) return { start: m.index, end: m.index + m[0].length, text: m[0] };
    seen += 1;
  }
  return null;
}

function findPlainImage(md, { src, index }) {
  const re = new RegExp(`!\\[([^\\]]*)\\]\\(${escapeRe(src)}\\)`, 'g');
  let m;
  let seen = 0;
  while ((m = re.exec(md)) !== null) {
    if (seen === index) return { start: m.index, end: m.index + m[0].length, alt: m[1] };
    seen += 1;
  }
  return null;
}


function findFigure(md, tagStart, tagEnd) {
  const openIdx = md.lastIndexOf('<div', tagStart);
  if (openIdx === -1) return null;
  const openEnd = md.indexOf('>', openIdx);
  if (openEnd === -1 || openEnd > tagStart) return null;

  if (md.slice(openEnd + 1, tagStart).trim() !== '') return null;
  const closeIdx = md.indexOf('</div>', tagEnd);
  if (closeIdx === -1) return null;
  return { start: openIdx, openEnd: openEnd + 1, end: closeIdx + 6, open: md.slice(openIdx, openEnd + 1) };
}


function applyMediaChanges(md, desc, changes) {
  if (!desc || !desc.src || !changes) return md;

  if (desc.kind === 'embed') {
    if (changes.width == null) return md;
    const esc = escapeRe(desc.src);
    const out = md.replace(
      new RegExp(`(:::video\\n(?:[^\\n]*\\n)*?url=${esc}\\n(?:[^\\n]*\\n)*?width=)[^\\n]*`, 'g'),
      `$1${changes.width}%`
    );
    if (out !== md) return out;
    // Blocks written before width was a property have none to replace.
    return md.replace(new RegExp(`(:::video\\nurl=${esc}\\n)`, 'g'), `$1width=${changes.width}%\n`);
  }

  let work  = md;
  let found = findMediaTag(work, desc);

  if (!found) {
    const plain = findPlainImage(work, desc);
    if (!plain) return md;
    // Promote to the canonical figure so the author's choice has somewhere to live.
    work  = work.slice(0, plain.start) + mediaFigure(desc.src, plain.alt || 'Image', 100, 'center').trim() + work.slice(plain.end);
    found = findMediaTag(work, desc);
    if (!found) return md;
  }

  let tagText = found.text;
  if (changes.width != null) tagText = setStyleProp(tagText, 'max-width', `${changes.width}%`);
  if (changes.alt   != null) tagText = setAttr(tagText, 'alt', changes.alt);
  work = work.slice(0, found.start) + tagText + work.slice(found.end);

  if (changes.align != null) {
    const fig = findFigure(work, found.start, found.start + tagText.length);
    if (fig) {
      const open = /text-align:\s*(left|center|right)/.test(fig.open)
        ? fig.open.replace(/text-align:\s*(left|center|right)/, () => `text-align:${changes.align}`)
        : setStyleProp(fig.open, 'text-align', changes.align);
      work = work.slice(0, fig.start) + open + work.slice(fig.openEnd);
    } else {
      // Imported media has no wrapper at all — give it one.
      work = `${work.slice(0, found.start)}<div class="${FIGURE_CLASS}" style="text-align:${changes.align};margin:1.25rem 0">${tagText}</div>${work.slice(found.start + tagText.length)}`;
    }
  }
  return work;
}


const GRIPS = [
  { id: 'nw', x: 0,  y: 0,  sx: -1, sy: -1, cur: 'nwse-resize' },
  { id: 'ne', x: 1,  y: 0,  sx:  1, sy: -1, cur: 'nesw-resize' },
  { id: 'sw', x: 0,  y: 1,  sx: -1, sy:  1, cur: 'nesw-resize' },
  { id: 'se', x: 1,  y: 1,  sx:  1, sy:  1, cur: 'nwse-resize' },
  { id: 'w',  x: 0,  y: .5, sx: -1, sy:  0, cur: 'ew-resize'   },
  { id: 'e',  x: 1,  y: .5, sx:  1, sy:  0, cur: 'ew-resize'   },
];

const WIDTH_PRESETS = [25, 50, 75, 100];

const TB_BTN  = 'px-2 py-1 rounded-md text-xs font-semibold transition-colors';
const TB_IDLE = 'text-slate-600 hover:bg-slate-100';
const TB_ON   = 'bg-indigo-600 text-white';

// Width, alignment and alt only. Replace and Delete are deliberately absent: an
// already-saved picture lives at an S3 URL the upload endpoint keeps no id for,
// so neither could clean up after itself. See the media notes before re-adding.
function MediaToolbar({ desc, rect, onChange }) {
  const [editingAlt, setEditingAlt] = useState(false);
  const [altDraft, setAltDraft]     = useState(desc.alt);

  const [widthDraft, setWidthDraft] = useState(String(desc.width));

  useEffect(() => { setAltDraft(desc.alt); setEditingAlt(false); }, [desc.src, desc.index, desc.alt]);
  useEffect(() => { setWidthDraft(String(desc.width)); }, [desc.width]);

  // Committed on blur or Enter, and clamped rather than silently ignored so an
  // out-of-range number still does something predictable.
  const commitWidth = () => {
    const v = Math.round(Number(widthDraft));
    if (!Number.isFinite(v) || widthDraft.trim() === '') { setWidthDraft(String(desc.width)); return; }
    const clamped = Math.min(100, Math.max(5, v));
    setWidthDraft(String(clamped));
    if (clamped !== desc.width) onChange({ width: clamped });
  };

  const GAP    = 10;
  const HEIGHT = 40;
  const below  = rect.top < HEIGHT + GAP + 8;
  const top    = below ? rect.top + rect.height + GAP : rect.top - HEIGHT - GAP;
  const left   = Math.max(8, Math.min(window.innerWidth - 340, rect.left));

  const commitAlt = () => { setEditingAlt(false); if (altDraft !== desc.alt) onChange({ alt: altDraft }); };

  return (
    <div
      data-media-ui
      className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl shadow-lg px-1.5 py-1"
      style={{ position: 'fixed', top, left, zIndex: 60 }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {editingAlt ? (
        <>
          <input
            autoFocus
            value={altDraft}
            onChange={(e) => setAltDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitAlt(); }
              if (e.key === 'Escape') { e.preventDefault(); setEditingAlt(false); setAltDraft(desc.alt); }
            }}
            placeholder="Describe this image…"
            className="w-56 px-2 py-1 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
          <button type="button" className={`${TB_BTN} ${TB_ON}`} onClick={commitAlt}>Done</button>
        </>
      ) : (
        <>
          {WIDTH_PRESETS.map((p) => (
            <button
              key={p} type="button" title={`${p}% of the content width`}
              className={`${TB_BTN} ${desc.width === p ? TB_ON : TB_IDLE}`}
              onClick={() => onChange({ width: p })}
            >
              {p}%
            </button>
          ))}
          <input
            type="number" min={5} max={100} value={widthDraft}
            title="Width, as a percent of the content column"
            onChange={(e) => setWidthDraft(e.target.value)}
            onBlur={commitWidth}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitWidth(); }
              if (e.key === 'Escape') { e.preventDefault(); setWidthDraft(String(desc.width)); }
            }}
            className="w-14 px-1.5 py-1 text-xs text-center border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
          {desc.kind !== 'embed' && (
            <>
              <span className="w-px h-5 bg-slate-200 mx-0.5" />
              {['left', 'center', 'right'].map((a) => (
                <button
                  key={a} type="button" title={`Align ${a}`}
                  className={`${TB_BTN} ${desc.align === a ? TB_ON : TB_IDLE} capitalize`}
                  onClick={() => onChange({ align: a })}
                >
                  {a[0].toUpperCase()}
                </button>
              ))}
              <span className="w-px h-5 bg-slate-200 mx-0.5" />
              <button type="button" title="Alt text" className={`${TB_BTN} ${desc.alt ? TB_IDLE : 'text-amber-600 hover:bg-amber-50'}`} onClick={() => setEditingAlt(true)}>
                Alt
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
MediaToolbar.propTypes = {
  desc:      PropTypes.shape({ kind: PropTypes.string, src: PropTypes.string, index: PropTypes.number, alt: PropTypes.string, align: PropTypes.string, width: PropTypes.number }).isRequired,
  rect:      PropTypes.shape({ top: PropTypes.number, left: PropTypes.number, width: PropTypes.number, height: PropTypes.number }).isRequired,
  onChange:  PropTypes.func.isRequired,
};

// ─── Markdown Preview ──────────────────────────────────────────────────────────
// "color:#ea580c;border-radius:3px" -> { color: '#ea580c', borderRadius: '3px' }
function cssToStyle(css) {
  const style = {};
  css.split(';').forEach((decl) => {
    const at = decl.indexOf(':');
    if (at < 1) return;
    const prop = decl.slice(0, at).trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    const val  = decl.slice(at + 1).trim();
    if (prop && val) style[prop] = val;
  });
  return style;
}

// processCustomBlocks writes colour and highlight as a link whose href carries
// the CSS, because a link is parsed inline everywhere markdown allows one while
// a raw <span> is torn into separate nodes by this version of react-markdown.
// Only that href is intercepted; every other link renders as it always did.
const MD_RENDERERS = {
  // href/title/target are picked out rather than spread: react-markdown also
  // passes internals such as `node`, and spreading those onto an <a> puts
  // node="[object Object]" in the DOM.
  link: ({ href, title, target, children }) => {
    const styled = /^#kss-style=(.*)$/.exec(href || '');
    if (!styled) return <a href={href} title={title} target={target}>{children}</a>;
    return <span style={cssToStyle(decodeURIComponent(styled[1]))}>{children}</span>;
  },
};

function MarkdownPreview({ content, bookMeta, onMediaChange, onDropMedia }) {
  const previewRef = useRef(null);   // the scrolling pane
  const contentRef = useRef(null);   // the .ebk column media is actually sized against
  const [sel, setSel]           = useState(null);  // a locator, re-resolved after every render
  const [rect, setRect]         = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const findEl = useCallback(() => {
    if (!sel || !contentRef.current) return null;
    return mediaPeers(contentRef.current, sel.kind, sel.src)[sel.index] || null;
  }, [sel]);


  const measure = useCallback(() => {
    const el = findEl();
    if (!el) { setRect(null); return; }
    const next = rectOf(el);

    setRect((prev) => (sameRect(prev, next) ? prev : next));
  }, [findEl]);

  const frame = useRef(0);
  const scheduleMeasure = useCallback(() => {
    if (frame.current) return;
    frame.current = window.requestAnimationFrame(() => { frame.current = 0; measure(); });
  }, [measure]);


  useEffect(() => {
    if (!sel) return undefined;
    // Capture phase: the pane itself scrolls, and a scroll there does not bubble.
    window.addEventListener('scroll', scheduleMeasure, true);
    window.addEventListener('resize', scheduleMeasure);
    return () => {
      window.removeEventListener('scroll', scheduleMeasure, true);
      window.removeEventListener('resize', scheduleMeasure);
      if (frame.current) { window.cancelAnimationFrame(frame.current); frame.current = 0; }
    };
  }, [sel, scheduleMeasure]);

  // Re-resolve and re-measure after every render of the content, so a rewrite
  // that replaces the node keeps the selection attached.
  useEffect(() => {
    if (!sel) { setRect(null); return; }
    measure();
  }, [sel, content, measure]);

  const select = useCallback((el) => {
    const desc = describeMedia(contentRef.current, el, contentRef.current);
    if (desc) setSel(desc);
  }, []);

  const handleClick = useCallback((e) => {
    const el = e.target.closest && e.target.closest(MEDIA_SELECTOR);
    if (el) select(el);
    else setSel(null);
  }, [select]);

 
  useEffect(() => {
    if (!sel) return undefined;
    const onDown = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-media-ui]')) return;
      if (contentRef.current && contentRef.current.contains(t)) return;
      setSel(null);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [sel]);


  const contentWidth = () => (contentRef.current ? contentRef.current.clientWidth : window.innerWidth);

  const startResize = useCallback((e, grip) => {
    e.preventDefault();
    e.stopPropagation();
    const el = findEl();
    if (!el) return;
    const target = resizeTargetOf(el);
    const cw     = contentWidth();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = target.offsetWidth;
    const ratio  = target.offsetWidth / Math.max(1, target.offsetHeight);

    const pctAt = (cx, cy) => {
      const d = ((cx - startX) * grip.sx) + ((cy - startY) * grip.sy * ratio);
      return Math.min(100, Math.max(5, ((startW + d) / cw) * 100));
    };
    const paint = (pct) => {
      if (target.tagName.toLowerCase() === 'iframe') target.style.width = `${pct}%`;
      else target.style.maxWidth = `${pct}%`;
      setRect(rectOf(target));
    };

    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not all inputs capture */ }

    const onMove = (mv) => paint(pctAt(mv.clientX, mv.clientY));
    const onUp = (mu) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const pct = Number(pctAt(mu.clientX, mu.clientY).toFixed(1));
      paint(pct);
      setSel((s) => (s ? { ...s, width: Math.round(pct) } : s));
      if (onMediaChange) onMediaChange(sel, { width: pct });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [findEl, onMediaChange, sel]);

  const change = useCallback((changes) => {
    if (!sel || !onMediaChange) return;
    setSel((s) => (s ? { ...s, ...changes } : s));
    onMediaChange(sel, changes);
  }, [sel, onMediaChange]);

  const processed = processCustomBlocks(content);
  return (
    <div
      ref={previewRef}
      onDragOver={(e) => { if (onDropMedia && e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        if (!onDropMedia || !e.dataTransfer.files?.length) return;
        e.preventDefault();
        setDragOver(false);
        onDropMedia(e.dataTransfer.files);
      }}
      style={{ position: 'relative' }}
    >
      <style>{PREVIEW_CSS}</style>
      <style>{`
        .ebk img,.ebk video{cursor:pointer}
        .ebk .ebook-video-wrap{padding:0 14px 14px 0;display:inline-block;max-width:100%}
        .ebk img:hover,.ebk video:hover,.ebk .ebook-video-wrap:hover{outline:2px dashed #c7d2fe;outline-offset:2px}
      `}</style>
      {dragOver && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-indigo-50 bg-opacity-80 border-4 border-dashed border-indigo-400 pointer-events-none">
          <span className="text-sm font-bold text-indigo-700">Drop images or video to add them here</span>
        </div>
      )}
      <div className="px-10 pt-10 pb-6 border-b border-slate-100" style={{ background: 'linear-gradient(135deg,#f0f4ff 0%,#fafafa 100%)' }}>
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-black text-slate-900 leading-tight mb-2">{bookMeta.title || 'Untitled eBook'}</h1>
            <div className="flex flex-wrap gap-2 mb-2">
              {bookMeta.subject    && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700">{bookMeta.subject}</span>}
              {bookMeta.classLevel && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-100 text-purple-700">{bookMeta.classLevel}</span>}
              {bookMeta.language   && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">{bookMeta.language}</span>}
              {bookMeta.board      && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">{bookMeta.board}</span>}
            </div>
            {bookMeta.description && <p className="text-sm text-slate-500 max-w-2xl">{bookMeta.description}</p>}
          </div>
        </div>
      </div>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div ref={contentRef} className="ebk px-10 py-8 max-w-4xl" onClick={handleClick}>
        <ReactMarkdown plugins={[gfm]} allowDangerousHtml renderers={MD_RENDERERS}>{processed}</ReactMarkdown>
      </div>

      {sel && rect && (
        <>
          {/* Selection box. pointer-events:none so a click inside still reaches
              the media underneath and keeps the selection alive. */}
          <div
            data-media-ui
            style={{
              position: 'fixed', left: rect.left - 2, top: rect.top - 2,
              width: rect.width + 4, height: rect.height + 4,
              border: '2px solid #4f46e5', borderRadius: 6,
              zIndex: 45, pointerEvents: 'none',
            }}
          />
          {GRIPS.map((g) => (
            /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
            <div
              key={g.id}
              data-media-ui
              onPointerDown={(e) => startResize(e, g)}
              title="Drag to resize"
              style={{
                position: 'fixed',
                left: rect.left + (rect.width * g.x) - 6,
                top:  rect.top  + (rect.height * g.y) - 6,
                width: 12, height: 12, borderRadius: 3, zIndex: 50,
                background: '#fff', border: '2px solid #4f46e5',
                boxShadow: '0 1px 4px rgba(15,23,42,.3)',
                cursor: g.cur, touchAction: 'none',
              }}
            />
          ))}
          <MediaToolbar
            desc={sel}
            rect={rect}
            onChange={change}
          />
        </>
      )}
    </div>
  );
}
MarkdownPreview.propTypes = {
  content:        PropTypes.string.isRequired,
  bookMeta:       PropTypes.shape({ title: PropTypes.string, subject: PropTypes.string, classLevel: PropTypes.string, language: PropTypes.string, board: PropTypes.string, description: PropTypes.string }).isRequired,
  onMediaChange:  PropTypes.func,
  onDropMedia:    PropTypes.func,
};
MarkdownPreview.defaultProps = { onMediaChange: null, onDropMedia: null };

// ─── Image Panel ───────────────────────────────────────────────────────────────
function ImagePanel({ onInsert, onClose, onPendingFile }) {
  const [blobUrl, setBlobUrl]   = useState('');
  const [alt, setAlt]           = useState('');
  const [align, setAlign]       = useState('center');
  const [size, setSize]         = useState('100%');
  const [caption, setCap]       = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const sizeOpts = [{ label: 'Small (40%)', val: '40%' }, { label: 'Medium (70%)', val: '70%' }, { label: 'Full (100%)', val: '100%' }];

  const pickFile = (file) => {
    if (!file || mediaKindOfFile(file) !== 'image') return;
    const local = URL.createObjectURL(file);
    setBlobUrl(local);
    onPendingFile(local, file);
  };
  const handleInsert = () => {
    if (!blobUrl) return;

    const capHtml = caption ? `<p style="font-size:.8rem;color:#64748b;margin-top:.5rem">${caption}</p>` : '';
    const figure  = mediaFigure(blobUrl, alt, parseInt(size, 10) || 100, align);

    onInsert(capHtml ? figure.replace('</div>', () => `${capHtml}</div>`) : figure);
  };
  return (
    <PanelWrapper title="Insert Image" icon={IC.image} onClose={onClose}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${dragOver ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 hover:border-indigo-400 bg-slate-50'}`}
      >
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
        {blobUrl
          ? <img src={blobUrl} alt="preview" className="max-h-32 mx-auto rounded-lg" />
          : (<><Ico d={IC.upload} size={24} cls="text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-500">Drag & drop or click to select</p></>)
        }
      </div>
      <FieldRow label="Alt Text"><input type="text" className={INPUT_CLS} placeholder="Describe the image…" value={alt} onChange={(e) => setAlt(e.target.value)} /></FieldRow>
      <FieldRow label="Caption (optional)"><input type="text" className={INPUT_CLS} placeholder="Figure 1: …" value={caption} onChange={(e) => setCap(e.target.value)} /></FieldRow>
      <FieldRow label="Size">
        <div className="flex gap-2">
          {sizeOpts.map((o) => (
            <button key={o.val} type="button" onClick={() => setSize(o.val)} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${size === o.val ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'}`}>{o.label}</button>
          ))}
        </div>
      </FieldRow>
      <FieldRow label="Alignment">
        <div className="flex gap-2">
          {['left', 'center', 'right'].map((a) => (
            <button key={a} type="button" onClick={() => setAlign(a)} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border capitalize transition-colors ${align === a ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'}`}>{a}</button>
          ))}
        </div>
      </FieldRow>
      <PanelFooter onClose={onClose} onInsert={handleInsert} disabled={!blobUrl} />
    </PanelWrapper>
  );
}
ImagePanel.propTypes = { onInsert: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired, onPendingFile: PropTypes.func.isRequired };

// ─── Video Panel ───────────────────────────────────────────────────────────────
function VideoPanel({ onInsert, onClose }) {
  const [url, setUrl]     = useState('');
  const [title, setTitle] = useState('');
  const [height, setH]    = useState('360');
  const heights           = ['240', '360', '480', '560'];
  const handleInsert = () => {
    if (!url.trim()) return;
    onInsert(`\n:::video\nurl=${url}\ntitle=${title || 'Video'}\nwidth=100%\nheight=${height}\n:::\n`);
  };
  return (
    <PanelWrapper title="Insert Video" icon={IC.video} onClose={onClose}>
      <FieldRow label="YouTube / Embed URL">
        <input id="vid-url" type="url" className={INPUT_CLS} placeholder="https://www.youtube.com/embed/..." value={url} onChange={(e) => setUrl(e.target.value)} />
        <p className="text-xs text-slate-400 mt-1">Use YouTube embed URL (Share → Embed → copy src)</p>
      </FieldRow>
      <FieldRow label="Title (optional)">
        <input id="vid-title" type="text" className={INPUT_CLS} placeholder="Video title..." value={title} onChange={(e) => setTitle(e.target.value)} />
      </FieldRow>
      <FieldRow label="Height (px)">
        <div className="flex gap-2">
          {heights.map((h) => (
            <button key={h} type="button" onClick={() => setH(h)} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${height === h ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'}`}>{h}px</button>
          ))}
        </div>
      </FieldRow>
      {url && <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-900"><iframe src={url} width="100%" height={height} title={title || 'preview'} frameBorder="0" allowFullScreen /></div>}
      <PanelFooter onClose={onClose} onInsert={handleInsert} disabled={!url.trim()} />
    </PanelWrapper>
  );
}
VideoPanel.propTypes = { onInsert: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired };

// ─── Button Panel ──────────────────────────────────────────────────────────────
function ButtonPanel({ onInsert, onClose }) {
  const [label, setLabel]   = useState('Download Notes');
  const [url, setUrl]       = useState('');
  const [bg, setBg]         = useState('#6366f1');
  const [fg, setFg]         = useState('#ffffff');
  const [size, setSize]     = useState('medium');
  const [radius, setRadius] = useState(8);
  const [align, setAlign]   = useState('left');
  const presetBgs = ['#6366f1','#2563eb','#10b981','#f59e0b','#ef4444','#0f172a','#6b7280'];
  let px;
  if (size === 'small') px = '.35rem .875rem';
  else if (size === 'large') px = '.875rem 2rem';
  else px = '.6rem 1.5rem';
  let pfs;
  if (size === 'small') pfs = '.8rem';
  else if (size === 'large') pfs = '1rem';
  else pfs = '.9rem';
  const handleInsert = () => onInsert(`\n:::button\nlabel=${label}\nurl=${url || '#'}\ncolor=${bg}\ntextColor=${fg}\nsize=${size}\nradius=${radius}\nalign=${align}\n:::\n`);
  return (
    <PanelWrapper title="Insert Button" icon={IC.btn} onClose={onClose}>
      <FieldRow label="Button Label"><input id="btn-label" type="text" className={INPUT_CLS} placeholder="Button text..." value={label} onChange={(e) => setLabel(e.target.value)} /></FieldRow>
      <FieldRow label="Link URL"><input id="btn-url" type="url" className={INPUT_CLS} placeholder="https://example.com" value={url} onChange={(e) => setUrl(e.target.value)} /></FieldRow>
      <FieldRow label="Background Color">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5 flex-wrap">
            {presetBgs.map((c) => (<button key={c} type="button" title={c} onClick={() => setBg(c)} style={{ background: c }} className={`w-7 h-7 rounded-full border-2 transition-all ${bg === c ? 'border-slate-700 scale-110' : 'border-transparent'}`} />))}
          </div>
          <input type="color" value={bg} onChange={(e) => setBg(e.target.value)} className="w-8 h-8 rounded cursor-pointer border border-slate-200" />
        </div>
      </FieldRow>
      <FieldRow label="Text Color">
        <div className="flex items-center gap-2">
          {['#ffffff','#0f172a','#fbbf24','#6366f1'].map((c) => (<button key={c} type="button" onClick={() => setFg(c)} style={{ background: c }} className={`w-7 h-7 rounded-full border-2 transition-all ${fg === c ? 'border-slate-700 scale-110' : 'border-slate-300'}`} />))}
          <input type="color" value={fg} onChange={(e) => setFg(e.target.value)} className="w-8 h-8 rounded cursor-pointer border border-slate-200" />
        </div>
      </FieldRow>
      <FieldRow label="Size">
        <div className="flex gap-2">
          {['small','medium','large'].map((s) => (<button key={s} type="button" onClick={() => setSize(s)} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border capitalize transition-colors ${size === s ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'}`}>{s}</button>))}
        </div>
      </FieldRow>
      <FieldRow label={`Border Radius: ${radius}px`}>
        <input id="btn-radius" type="range" min={0} max={50} value={radius} onChange={(e) => setRadius(Number(e.target.value))} className="w-full accent-indigo-600" />
      </FieldRow>
      <FieldRow label="Alignment">
        <div className="flex gap-2">
          {['left','center','right'].map((a) => (<button key={a} type="button" onClick={() => setAlign(a)} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border capitalize transition-colors ${align === a ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'}`}>{a}</button>))}
        </div>
      </FieldRow>
      <div className="p-4 rounded-xl border border-dashed border-slate-300 bg-slate-50" style={{ textAlign: align }}>
        <span style={{ background: bg, color: fg, padding: px, borderRadius: `${radius}px`, fontWeight: 600, fontSize: pfs, display: 'inline-block', cursor: 'pointer' }}>{label || 'Button'}</span>
      </div>
      <PanelFooter onClose={onClose} onInsert={handleInsert} />
    </PanelWrapper>
  );
}
ButtonPanel.propTypes = { onInsert: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired };

// ─── Lifecycle Builder Panel ───────────────────────────────────────────────────
function LifecyclePanel({ onInsert, onClose, onPendingFile }) {
  const [title, setTitle]       = useState('');
  const [circular, setCircular] = useState(true);
  const [stages, setStages]     = useState([
    { name: 'Stage 1', emoji: '🥚', img: null, blobUrl: '' },
    { name: 'Stage 2', emoji: '🐛', img: null, blobUrl: '' },
    { name: 'Stage 3', emoji: '🦋', img: null, blobUrl: '' },
  ]);
  const fileRefs = useRef([]);
  const addStage = () => setStages((p) => [...p, { name: `Stage ${p.length + 1}`, emoji: '🔵', img: null, blobUrl: '' }]);
  const removeStage = (i) => setStages((p) => p.filter((_, idx) => idx !== i));
  const updateStage = (i, key, val) => setStages((p) => p.map((s, idx) => idx === i ? { ...s, [key]: val } : s));
  const pickImg = (i, file) => {
    if (!file || mediaKindOfFile(file) !== 'image') return;
    const blobUrl = URL.createObjectURL(file);
    onPendingFile(blobUrl, file);
    updateStage(i, 'blobUrl', blobUrl);
    updateStage(i, 'img', blobUrl);
  };
  const handleInsert = () => {
    const stageLines = stages.map((s, i) => {
      const imgPart = s.blobUrl ? `|${s.emoji}|${s.blobUrl}` : `|${s.emoji}`;
      return `${i + 1}. ${s.name}${imgPart}`;
    }).join('\n');
    onInsert(`\n:::lifecycle\ntitle=${title || 'Life Cycle'}\ncircular=${circular}\n${stageLines}\n:::\n`);
  };
  const COMMON_EMOJIS = ['🥚','🐛','🦋','🐟','🐸','🌱','🌿','🌳','🌸','🍎','🔵','🟢','🔴','⚗️','🧬','☀️','☁️','🌧️','🌊','🔥','💎','🌋'];
  return (
    <PanelWrapper title="Life Cycle Builder" icon={IC.cycle} onClose={onClose}>
      <FieldRow label="Life Cycle Title">
        <input type="text" className={INPUT_CLS} placeholder="e.g. Life Cycle of a Butterfly" value={title} onChange={(e) => setTitle(e.target.value)} />
      </FieldRow>
      <FieldRow label="Type">
        <div className="flex gap-2">
          <button type="button" onClick={() => setCircular(true)} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${circular ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'}`}>↺ Circular (repeating)</button>
          <button type="button" onClick={() => setCircular(false)} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${!circular ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'}`}>→ Linear (one-way)</button>
        </div>
      </FieldRow>
      <div className="flex flex-col gap-2 max-h-64 overflow-y-auto pr-1">
        {stages.map((s, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="border border-slate-200 rounded-xl p-3 bg-slate-50 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-400 flex-shrink-0 w-14">Step {i + 1}</span>
              <input type="text" className="flex-1 min-w-0 text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-400 bg-white" placeholder="Stage name" value={s.name} onChange={(e) => updateStage(i, 'name', e.target.value)} />
              <button type="button" onClick={() => removeStage(i)} className="text-red-400 hover:text-red-600 flex-shrink-0"><Ico d={IC.x} size={13} /></button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400 w-14">Emoji</span>
              <div className="flex flex-wrap gap-1">
                {COMMON_EMOJIS.map((em) => (
                  <button key={em} type="button" onClick={() => updateStage(i, 'emoji', em)} className={`w-7 h-7 text-sm rounded border transition-all ${s.emoji === em ? 'border-indigo-500 bg-indigo-50 scale-110' : 'border-transparent hover:border-slate-300'}`}>{em}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400 w-14">Image</span>
              <input ref={(el) => { fileRefs.current[i] = el; }} type="file" accept="image/*" className="hidden" onChange={(e) => pickImg(i, e.target.files?.[0])} />
              <button type="button" onClick={() => fileRefs.current[i]?.click()} className="text-xs px-3 py-1.5 rounded-lg border border-dashed border-slate-300 hover:border-indigo-400 text-slate-500 hover:text-indigo-600 transition-colors">
                {s.blobUrl ? '✓ Image selected' : '+ Upload image (optional)'}
              </button>
              {s.blobUrl && <img src={s.blobUrl} alt={s.name} className="w-10 h-10 rounded-full object-cover border-2 border-emerald-400" />}
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={addStage} className="flex items-center gap-2 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors border border-dashed border-indigo-300 rounded-xl px-3 py-2 hover:border-indigo-500">
        <Ico d={IC.plus} size={13} /> Add Stage
      </button>
      <div className="border border-slate-200 rounded-xl p-3 bg-white">
        <p className="text-xs font-bold text-slate-500 mb-2">Preview</p>
        <div className="flex items-center gap-2 flex-wrap">
          {stages.map((s, i) => (
            <React.Fragment key={s.name + String(i)}>
              <div className="flex flex-col items-center gap-1 min-w-14">
                {s.blobUrl
                  ? <img src={s.blobUrl} alt={s.name} className="w-10 h-10 rounded-full object-cover border-2 border-emerald-400" />
                  : <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-lg border-2 border-emerald-300">{s.emoji}</div>
                }
                <span className="text-xs text-center text-slate-600 leading-tight max-w-16">{s.name}</span>
              </div>
              {i < stages.length - 1 && <span className="text-emerald-500 font-bold">{circular && i === stages.length - 2 ? '↺' : '→'}</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
      <PanelFooter onClose={onClose} onInsert={handleInsert} disabled={stages.length === 0} />
    </PanelWrapper>
  );
}
LifecyclePanel.propTypes = { onInsert: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired, onPendingFile: PropTypes.func.isRequired };

// content tab 

function ContentTabsSection({ selectedEbooks, selectedCourses, selectedAssessments, setSelectedEbooks, setSelectedCourses, setSelectedAssessments }) {
    const [contentTab, setContentTab] = useState('ebooks');
    const [ebooks, setEbooks] = useState([]);
    const [courses, setCourses] = useState([]);
    const [assessments, setAssessments] = useState([]);
    const [contentLoading, setContentLoading] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        const fetch = async () => {
            setContentLoading(true);
            try {
                const [ebookRes, courseRes, assessmentRes] = await Promise.all([
                    adminServices.getEbooks('', 1, 500),
                    adminServices.getLookUpData(['course']),
                    adminServices.getGenericAssessmentDropDown({ page: 1, pageSize: 500, audience: 'B2C' }),
                ]);
                if (ebookRes.ok) setEbooks(Array.isArray(ebookRes.data) ? ebookRes.data : ebookRes.data?.rows || []);
                if (courseRes.ok) setCourses((courseRes.data.course || []).filter(c => Number(c.status) === 1));
                if (assessmentRes.ok) {
                    const rows = Array.isArray(assessmentRes.data?.rows) ? assessmentRes.data.rows : assessmentRes.data || [];
                    setAssessments(rows);
                }
            } catch (e) { console.error(e); }
            setContentLoading(false);
        };
        fetch();
    }, []);

    const items = (contentTab === 'ebooks' ? ebooks : contentTab === 'courses' ? courses : assessments)
        .filter(i => (i.title || i.name || '').toLowerCase().includes(search.toLowerCase()));
    const selected = contentTab === 'ebooks' ? selectedEbooks : contentTab === 'courses' ? selectedCourses : selectedAssessments;
    const setSelected = contentTab === 'ebooks' ? setSelectedEbooks : contentTab === 'courses' ? setSelectedCourses : setSelectedAssessments;
    const toggleId = (id) => setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

    return (
        <>
            <div className="flex border-b border-slate-200 mb-2">
                {[['ebooks','E-Books'],['courses','Courses'],['assessments','Assessments']].map(([key, label]) => (
                    <button key={key} type="button" onClick={() => { setContentTab(key); setSearch(''); }}
                        className={`px-4 py-2 text-[13px] font-medium bg-transparent border-0 border-b-2 -mb-px transition-colors ${contentTab === key ? 'text-indigo-600 border-indigo-600' : 'text-slate-500 border-transparent'}`}>
                        {label}
                    </button>
                ))}
            </div>
            <input className={`${INPUT_CLS} mb-2`} placeholder={`Search ${contentTab}...`} value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                {contentLoading
                    ? <p className="text-center text-xs text-slate-400 py-4">Loading...</p>
                    : items.length === 0
                        ? <p className="text-center text-xs text-slate-400 py-4">No items found</p>
                        : items.map(item => (
                            <div key={item.id} onClick={() => toggleId(item.id)}
                                className="flex items-center px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                                <input type="checkbox" readOnly checked={selected.includes(item.id)} className="mr-2 accent-indigo-600" />
                                <span className="text-sm text-slate-800">{item.title || item.name}</span>
                            </div>
                        ))
                }
            </div>
        </>
    );
}

// ─── Metadata Modal ────────────────────────────────────────────────────────────
// ─── Assessment Chapter Field (inline, for the Metadata modal) ────────────────
// Same vocabulary as the sidebar picker, but at book level: "this whole eBook
// covers these assessment chapters."
function AssessmentChapterField({ options, value, onChange }) {
  const [q, setQ] = useState('');
  const selected = Array.isArray(value) ? value : [];
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? options.filter((o) => `${o.chapterName || ''} ${o.sourceTitle || ''}`.toLowerCase().includes(needle))
    : options;
  const toggle = (id) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div>
      <input
        className={`${INPUT_CLS} mb-2`}
        placeholder="Search assessment chapters..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="border border-slate-200 rounded-lg max-h-44 overflow-y-auto">
        {shown.length === 0 && (
          <p className="text-center text-xs text-slate-400 py-6">
            {options.length === 0
              ? 'No assessment chapters yet. Create them under Assessments first.'
              : 'No chapter matches that search.'}
          </p>
        )}
        {shown.map((o) => {
          const on = draft.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => toggle(o.id)}
              className={`w-full flex items-start gap-2 px-3 py-2 text-left border-0 border-b border-slate-100 last:border-0 cursor-pointer transition-colors ${on ? 'bg-indigo-50' : 'bg-white hover:bg-slate-50'}`}
            >
              <span className={`mt-0.5 w-3.5 h-3.5 rounded border shrink-0 flex items-center justify-center ${on ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
                {on && <Ico d="M20 6L9 17l-5-5" size={9} cls="text-white" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className={`block text-[12.5px] leading-tight truncate ${on ? 'font-semibold text-indigo-900' : 'text-slate-700'}`}>
                  {o.chapterName}
                </span>
                <span className="block text-[10.5px] text-slate-400 truncate">
                  {o.sourceTitle || 'No source'} · {o.questionCount || 0} questions
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-[10.5px] text-slate-400">
        {selected.length
          ? `${selected.length} chapter${selected.length > 1 ? 's' : ''} linked to this book`
          : 'Link the assessment chapters this book teaches, so wrong answers can point here.'}
      </p>
    </div>
  );
}
AssessmentChapterField.propTypes = {
  options:  PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, chapterName: PropTypes.string })).isRequired,
  value:    PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
};

function MetadataModal({ 
    metadata, onChange, onClose,
    coverImage, coverImgPreview,     
    setCoverImage, setCoverImgPreview, 
    selectedEbooks, selectedCourses, selectedAssessments,
    setSelectedEbooks, setSelectedCourses, setSelectedAssessments,ebookId,
    assessmentChapters, selectedChapters, setSelectedChapters
}) { 
  const [coverImageError, setCoverImageError] = useState('');
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaError, setMetaError] = useState('');

  // eBook "series" = an assessment type (NEET / JEE / …), the same list the Test
  // Series screen is built from. One per book, or none ("Other"). Seeded from the
  // redux slice the student app fills on login; fetched directly here because the
  // admin app doesn't populate that slice.
  const reduxAssessmentTypes = useSelector((state) => state.AssessmentType);
  const [assessmentTypes, setAssessmentTypes] = useState(
    Array.isArray(reduxAssessmentTypes) ? reduxAssessmentTypes : []
  );
  useEffect(() => {
    if (assessmentTypes.length) return;
    (async () => {
      try {
        const res = await adminServices.getAssessmentTypeDropdown();
        const rows = res?.data?.data ?? res?.data ?? [];
        if (Array.isArray(rows)) setAssessmentTypes(rows);
      } catch (err) {
        console.error('Failed to load assessment types:', err);
      }
    })();
  }, []);
  // Subjects offered for the chosen series come from configuration
  // (public.chapter_lk via /assessment/assessment-subject) — not the hardcoded
  // SUBJECTS list, which is the fallback when no series is picked.
  const [subjectOptions, setSubjectOptions] = useState(SUBJECTS);
  useEffect(() => {
    let cancelled = false;
    if (!metadata.examTypeId) { setSubjectOptions(SUBJECTS); return undefined; }
    (async () => {
      try {
        const res = await adminServices.getAssessmentSubject(metadata.examTypeId);
        const rows = res?.data?.data ?? res?.data ?? [];
        if (!cancelled) {
          const names = rows.map((r) => r.name).filter(Boolean);
          setSubjectOptions(names.length ? names : SUBJECTS);
        }
      } catch (err) {
        console.error('Failed to load series subjects:', err);
        if (!cancelled) setSubjectOptions(SUBJECTS);
      }
    })();
    return () => { cancelled = true; };
  }, [metadata.examTypeId]);

  const imageWidthAndHeight = (provideFile) => {
    if (!provideFile) return Promise.resolve({ width: null, height: null });
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.src = reader.result;
      };
      reader.readAsDataURL(provideFile);
    });
  };

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    setCoverImage(null); setCoverImgPreview(null); setCoverImageError('');
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/bmp'].includes(file.type)) { setCoverImageError('Only JPG, PNG, BMP allowed'); return; }
    if (file.size > 104857600) { setCoverImageError('Max file size is 100 MB'); return; }
    imageWidthAndHeight(file).then((res) => {
      if (res.width >= 1200 && res.height >= 400) { setCoverImage(file); setCoverImgPreview(URL.createObjectURL(file)); }
      else setCoverImageError('Image must be at least 1200 × 400 px');
    });
  };

  const renderSelect = (id, lbl, key, opts) => (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{lbl}</label>
      <select id={id} className={INPUT_CLS} value={metadata[key]} onChange={(e) => onChange(key, e.target.value)}>
        <option value="">Select {lbl}</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
  const renderInput = (id, lbl, key, type) => (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{lbl}</label>
      <input id={id} type={type || 'text'} className={INPUT_CLS} placeholder={`Enter ${lbl.toLowerCase()}...`} value={metadata[key]} onChange={(e) => onChange(key, e.target.value)} />
    </div>
  );

  const handleSaveMeta = async () => {
    setMetaSaving(true);
    setMetaError('');
    try {
      const fd = new FormData();
      fd.append('title', metadata.title || '');
      if (metadata.subject) fd.append('subjectId', metadata.subject);
      if (metadata.classLevel) fd.append('classLevel', metadata.classLevel);
      if (metadata.board) fd.append('board', metadata.board);
      fd.append('language', metadata.language || 'English');
      if (metadata.description) fd.append('description', metadata.description);
      if (metadata.shortDescription) fd.append('shortDescription', metadata.shortDescription);
      fd.append('relatedContent', JSON.stringify({
        courses: selectedCourses.map(id => ({ id })),
        assessments: selectedAssessments.map(id => ({ id })),
        ebooks: selectedEbooks.map(id => ({ id })),
      }));
      // Always sent, so clearing every chapter actually clears them — the API
      // treats an absent field as "leave the tags alone".
      fd.append('relatedChapterId', JSON.stringify(selectedChapters || []));
      // Always sent: '' clears the series back to "Other".
      fd.append('examTypeId', metadata.examTypeId || '');
      if (coverImage instanceof File) fd.append('coverImage', coverImage);

      await adminServices.updateEbook(ebookId, fd);
      setMetaSaving(false);
      onClose();
    } catch (err) {
      console.error('Save metadata failed:', err);
      setMetaError(err?.response?.data?.message || 'Could not save the metadata. Please try again.');
      setMetaSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      {/* eBooks -> Edit eBook -> Metadata: at 1360x768 the header was cut off, because the MUI AppBar is fixed at z-index 1100 while this overlay was z-50. */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-white rounded-t-2xl shrink-0">
          <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shrink-0"><Ico d={IC.book} size={20} cls="text-white" /></div>
          <div className="min-w-0"><h3 className="text-lg font-black text-slate-900">Book Metadata</h3><p className="text-xs text-slate-500 truncate">Details shown to students in the library</p></div>
          <button type="button" onClick={onClose} className="ml-auto shrink-0 text-slate-400 hover:text-slate-700"><Ico d={IC.x} size={20} /></button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4 overflow-y-auto flex-1 min-h-0">
          <div className="col-span-2">{renderInput('m-title', 'Book Title', 'title')}</div>
          <div className="col-span-2 flex flex-col gap-1.5">
            <label htmlFor="m-series" className="text-xs font-semibold text-slate-500 uppercase tracking-wide">eBook Series</label>
            <select
              id="m-series"
              className={INPUT_CLS}
              value={metadata.examTypeId || ''}
              onChange={(e) => onChange('examTypeId', e.target.value)}
            >
              <option value="">Other (no series)</option>
              {assessmentTypes
                .filter((t) => t.is_active)
                .map((t) => (
                  <option key={t.id} value={t.id}>{t.code || t.description || `Series ${t.id}`}</option>
                ))}
            </select>
          </div>
          {renderSelect('m-sub', 'Subject', 'subject', subjectOptions)}
          {renderSelect('m-cls', 'Class Level', 'classLevel', CLASS_LEVELS)}
          {renderSelect('m-lang', 'Language', 'language', LANGUAGES)}
          {renderSelect('m-brd', 'Board Type', 'board', BOARDS)}
          <div className="col-span-2 flex flex-col gap-1.5">
            <label htmlFor="m-desc" className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</label>
            <textarea id="m-desc" rows={3} className={`${INPUT_CLS} resize-none`} placeholder="Brief description shown to students..." value={metadata.description} onChange={(e) => onChange('description', e.target.value)} />
          </div>
          <div className="col-span-2 flex flex-col gap-1.5">
            <label htmlFor="m-short-desc" className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Short Description</label>
            <input id="m-short-desc" type="text" className={INPUT_CLS} placeholder="Brief tagline..." value={metadata.shortDescription} onChange={(e) => onChange('shortDescription', e.target.value)} />
          </div>
          {/* Assessment chapters this book covers */}
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">Assessment Chapters</label>
            <AssessmentChapterField
              options={assessmentChapters || []}
              value={selectedChapters || []}
              onChange={setSelectedChapters}
            />
          </div>
          {/* Content Tabs */}
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">Related Content</label>
            <ContentTabsSection
              selectedEbooks={selectedEbooks}
              selectedCourses={selectedCourses}
              selectedAssessments={selectedAssessments}
              setSelectedEbooks={setSelectedEbooks}
              setSelectedCourses={setSelectedCourses}
              setSelectedAssessments={setSelectedAssessments}
            />
          </div>
          { }
          <div className="col-span-2 flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Cover Image</label>
            <CoverImageUpload
              existingUrl={coverImgPreview}
              coverImage={coverImage}
              preview={coverImgPreview}
              onFileChange={handleFileChange}
              onRemove={() => { setCoverImage(null); setCoverImgPreview(null); setCoverImageError(''); }}
              error={coverImageError}
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 shrink-0 border-t border-slate-100">
          {metaError && <span className="mr-auto text-xs text-red-600">{metaError}</span>}
          <button type="button" onClick={onClose} disabled={metaSaving} className="px-5 py-2 rounded-xl text-sm font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors">Cancel</button>
          <button type="button" onClick={handleSaveMeta} disabled={metaSaving} className="px-6 py-2 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm">{metaSaving ? 'Saving…' : 'Save Metadata'}</button>
        </div>
      </div>
    </div>
  );
}
MetadataModal.propTypes = {
  metadata: PropTypes.shape({ title: PropTypes.string, subject: PropTypes.string, classLevel: PropTypes.string, language: PropTypes.string, board: PropTypes.string, description: PropTypes.string, author: PropTypes.string }).isRequired,
  onChange: PropTypes.func.isRequired,
  onClose:  PropTypes.func.isRequired,
  ebookId: PropTypes.string.isRequired,
  assessmentChapters: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, chapterName: PropTypes.string })),
  selectedChapters: PropTypes.arrayOf(PropTypes.number),
  setSelectedChapters: PropTypes.func.isRequired,
};
MetadataModal.defaultProps = { assessmentChapters: [], selectedChapters: [] };

// ─── Assessment Chapter Picker ─────────────────────────────────────────────────
// Tags an eBook chapter to the assessment chapter(s) it teaches — the same
// vocabulary questions are tagged with (public.assessment_modules), which is what
// lets a wrong answer be traced back to the right chapter to read.
//
// Chapter names repeat across assessment sources ("Kinematics" exists under
// several), so every row shows its source title underneath; without it the list
// is a wall of indistinguishable duplicates.
const PICKER_HEIGHT = 320;
const PICKER_MIN_W  = 340;

function AssessmentChapterPicker({ anchorEl, options, value, onChange, onClose }) {
  const [q, setQ] = useState('');
  const [box, setBox] = useState(null);
  // Ticking a row only edits this draft. Nothing is sent until Done, so closing
  // with the X leaves the chapter's existing links untouched. Seeded on mount,
  // and the panel unmounts on close, so every open starts from what is saved.
  const [draft, setDraft] = useState(() => (Array.isArray(value) ? value : []));


  useEffect(() => {
    if (!anchorEl) return undefined;
    const place = () => {
      const r = anchorEl.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - 12;
      const openDown = below >= Math.min(PICKER_HEIGHT, above) || below >= 200;
  
      const w = Math.min(Math.max(r.width, PICKER_MIN_W), window.innerWidth - 16);
      setBox({
        left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)),
        width: w,
        top: openDown ? r.bottom + 4 : Math.max(8, r.top - Math.min(PICKER_HEIGHT, above) - 4),
        maxHeight: Math.max(160, Math.min(PICKER_HEIGHT, openDown ? below : above)),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchorEl]);

  const needle = q.trim().toLowerCase();
  const shown = needle
    ? options.filter((o) =>
        `${o.chapterName || ''} ${o.sourceTitle || ''}`.toLowerCase().includes(needle))
    : options;

  const toggle = (id) =>
    setDraft((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));
  const commit = () => { onChange(draft); onClose(); };

  if (!box) return null;

  return createPortal(
    <div
      data-portal-dropdown
      style={{ position: 'fixed', top: box.top, left: box.left, width: box.width, maxHeight: box.maxHeight, zIndex: 9999 }}
      className="bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden flex flex-col">
      <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 flex-shrink-0">
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          className="flex-1 min-w-0 text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-400"
          placeholder="Search assessment chapters..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
          <Ico d={IC.x} size={13} />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {shown.length === 0 && (
          <p className="px-3 py-4 text-[11px] text-slate-400 text-center">
            {options.length === 0
              ? 'No assessment chapters found. Create them under Assessments first.'
              : 'No chapter matches that search.'}
          </p>
        )}
        {shown.map((o) => {
          const on = draft.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => toggle(o.id)}
              className={`w-full flex items-start gap-2 px-3 py-2 text-left border-none cursor-pointer transition-colors ${on ? 'bg-indigo-50' : 'bg-white hover:bg-slate-50'}`}
            >
              <span className={`mt-0.5 w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${on ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
                {on && <Ico d="M20 6L9 17l-5-5" size={9} cls="text-white" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className={`block text-[11.5px] leading-snug break-words ${on ? 'font-semibold text-indigo-900' : 'text-slate-700'}`}>
                  {o.chapterName}
                </span>
                <span className="block text-[10px] text-slate-400 truncate">
                  {o.sourceTitle || 'No source'} · {o.questionCount || 0} questions
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="px-3 py-2 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
        <span className="text-[10px] text-slate-400">{draft.length} selected</span>
        <button type="button" onClick={commit} className="text-[11px] font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-3 py-1 border-none cursor-pointer">
          Done
        </button>
      </div>
    </div>,
    document.body
  );
}
AssessmentChapterPicker.propTypes = {
  anchorEl: PropTypes.instanceOf(Element),
  options:  PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, chapterName: PropTypes.string, sourceTitle: PropTypes.string })).isRequired,
  value:    PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
  onClose:  PropTypes.func.isRequired,
};
AssessmentChapterPicker.defaultProps = { anchorEl: null };

// ─── Chapter Import Modal ──────────────────────────────────────────────────────
// The sibling of "Build it manually": drop a Word document and it becomes one new
// chapter (with its topics) appended to this book. Uses the chapter-scoped import
// endpoints, so it never touches the rest of the book.
function ChapterImportModal({ ebookId, onClose, onImported }) {
  const [docFile, setDocFile]   = useState(null);
  const [docError, setDocError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy]         = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const inputRef = useRef(null);

  const pickDocument = (file) => {
    setDocFile(null); setDocError('');
    if (!file) return;
    if (file.name.toLowerCase().endsWith('.doc')) {
      setDocError('Legacy .doc files are not supported. Save it as .docx and try again.');
      return;
    }
    const isDocx = file.name.toLowerCase().endsWith('.docx')
      && (!file.type || file.type === DOCX_MIME || file.type === 'application/octet-stream');
    if (!isDocx) { setDocError('Only Word documents (.docx) can be uploaded.'); return; }
    if (file.size > MAX_DOC_MB * 1024 * 1024) { setDocError(`Document must be under ${MAX_DOC_MB} MB.`); return; }
    setDocFile(file);
  };

  const handleImport = async () => {
    if (!docFile) { setDocError('Please choose a Word document.'); return; }
    setBusy(true); setDocError('');
    try {
      let res;
      if (docFile.size <= DIRECT_UPLOAD_LIMIT) {
        const fd = new FormData();
        fd.append('document', docFile);
        res = await adminServices.importEbookChapterDocument(ebookId, fd, (evt) => {
          if (evt?.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
        });
      } else {
    
        setUploadPct(1);
        const urlRes = await adminServices.getEbookChapterImportUploadUrl(ebookId, docFile.name);
        if (!urlRes.ok || !urlRes.data?.url) {
          setDocError(urlRes?.data?.message || 'Could not start the upload.'); setBusy(false); return;
        }
        const s3Res = await adminServices.putFileToUrl(urlRes.data.url, docFile, (evt) => {
          if (evt?.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
        });
        if (!s3Res.ok) { setDocError('The document upload failed. Please try again.'); setBusy(false); return; }
        setUploadPct(100);
        res = await adminServices.importEbookChapterDocumentByKey(ebookId, {
          objectKey: urlRes.data.key, fileName: docFile.name, fileSize: docFile.size,
        });
      }
      if (res && res.ok === false) {
        setDocError(res?.data?.message || 'The document could not be imported.'); setBusy(false); return;
      }
      // Endpoint returns the created chapter (or { chapter } / [chapters]).
      const payload = res?.data?.chapter ?? res?.data?.chapters ?? res?.data ?? res;
      await onImported(payload);
      onClose();
    } catch (err) {
      setDocError(err?.response?.data?.message || 'Failed to import the document.');
      setBusy(false);
    }
  };

  return (
    <PanelWrapper title="Import Chapter from Document" icon={IC.upload} onClose={busy ? () => {} : onClose}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); pickDocument(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-emerald-500 bg-emerald-50'
            : docFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-emerald-400 bg-slate-50'}`}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(e) => pickDocument(e.target.files?.[0])}
        />
        {docFile
          ? <p className="text-sm font-semibold text-emerald-800">{docFile.name} <span className="text-emerald-600 font-normal">({(docFile.size / (1024 * 1024)).toFixed(1)} MB)</span></p>
          : (<><Ico d={IC.upload} size={24} cls="text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-500">Drag &amp; drop your .docx, or click to browse</p><p className="text-xs text-slate-400 mt-1">Word documents only, up to {MAX_DOC_MB} MB</p></>)
        }
      </div>
      {docError && <p className="text-xs text-red-600">{docError}</p>}
      <p className="text-xs text-slate-400 leading-relaxed">
        The document&apos;s headings become topics under one new chapter. Images, tables
        and equations are carried across, and you can edit everything afterwards.
      </p>
      <div className="flex gap-3 pt-2 border-t border-slate-100">
        <button type="button" onClick={onClose} disabled={busy} className="flex-1 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors">Cancel</button>
        <button type="button" onClick={handleImport} disabled={busy || !docFile} className="flex-1 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          {busy ? (uploadPct > 0 && uploadPct < 100 ? `Uploading ${uploadPct}%…` : 'Importing…') : 'Import'}
        </button>
      </div>
    </PanelWrapper>
  );
}
ChapterImportModal.propTypes = {
  ebookId:    PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose:    PropTypes.func.isRequired,
  onImported: PropTypes.func.isRequired,
};

// ─── Chapter Sidebar ───────────────────────────────────────────────────────────
function ChapterSidebar({ chapters, setChapters, ebookId, activeTopic, onTopicSelect, assessmentChapters }) {
  const [importing, setImporting] = useState(false);
  // { kind: 'chapter' | 'topic', chId, topId, title } while a delete awaits confirmation.
  const [pendingDel, setPendingDel] = useState(null);
  // In-flight guards, both null when idle: `adding` holds the title of the
  // chapter being created (the placeholder row shows it), `addingTopic` the id
  // of the chapter whose topic is being created. They block the repeat clicks
  // that duplicated rows, and hold the text so a failed create can return it.
  const [adding, setAdding]           = useState(null);
  const [addingTopic, setAddingTopic] = useState(null);
  const [newTitle, setNew]      = useState('');
  const [editingId, setEditing] = useState(null);
  const [editVal, setEditVal]   = useState('');
  const [topicIn, setTopicIn]   = useState({});
  // Chapters chosen for the chapter about to be created, and which existing
  // chapter (if any) has its picker open.
  const [newLinks, setNewLinks] = useState([]);
  const [pickerFor, setPicker]  = useState(null); // chapter id, or 'new'
  // The element each picker measures itself against, keyed by chapter id (and
  // 'new' for the footer). Needed because the panel is portalled to body.
  const anchorRefs = useRef({});
  // { chId, message } when a link save was rejected and rolled back.
  const [linkError, setLinkError] = useState(null);

  const chapterNameById = React.useMemo(() => {
    const m = new Map();
    assessmentChapters.forEach((c) => m.set(c.id, c));
    return m;
  }, [assessmentChapters]);

  // Names for a chapter's tags. Prefers what the picker list holds, falling back
  // to the names the API resolved on load — the picker is capped at 200 rows, so
  // a book tagged to a chapter outside that window would otherwise show nothing.
  const linkNames = (ch) => (ch.relatedChapterId || []).map((cid) => {
    const fromOptions = chapterNameById.get(cid);
    if (fromOptions) return fromOptions.chapterName;
    const fromServer = (ch.relatedChapters || []).find((r) => r.id === cid);
    return fromServer?.chapterName || `#${cid}`;
  });

  // Tagging and untagging both save on the spot — there is no Save button for
  // links, and nothing is held until the eBook is saved.
  const saveLinks = async (chId, ids) => {
    const before = chapters.find((c) => c.id === chId)?.relatedChapterId || [];
    // Update locally first so the chips respond immediately, then persist.
    setChapters((p) => p.map((c) => (c.id === chId ? { ...c, relatedChapterId: ids } : c)));
    setLinkError(null);
    try {
      const res = await adminServices.updateEbookChapter(chId, { relatedChapterId: ids });
      // apisauce resolves on HTTP errors instead of throwing, so the catch alone
      // never saw a rejected id. The optimistic chip then stayed on screen while
      // the database had nothing — the link looked saved and was not.
      if (res?.ok === false) throw new Error(res?.data?.message || 'Could not save the link.');
    } catch (err) {
      console.error('updateChapter links failed:', err);
      setChapters((p) => p.map((c) => (c.id === chId ? { ...c, relatedChapterId: before } : c)));
      setLinkError({ chId, message: err.message || 'Could not save the link.' });
    }
  };

  const addCh = async () => {
    const title = newTitle.trim();
    // `adding` holds the pending title, which both blocks the repeat clicks that
    // used to create duplicates and gives the placeholder row something to show.
    if (adding || !title) return;
    const links = newLinks;
    // Emptied straight away so the box is free for the next chapter. The title
    // is not lost: it lives in `adding` until the server confirms, and comes
    // back to the box if the request fails.
    setAdding(title);
    setNew('');
    try {
      const res = await adminServices.createEbookChapter(ebookId, {
        title,
        chapterNumber: chapters.length + 1,
        orderIndex: chapters.length,
        relatedChapterId: links,
      });
      // apisauce resolves on HTTP errors instead of throwing, so without this a
      // failed create fell through and appended a chapter with an undefined id.
      const ch = res?.ok === false ? null : (res?.data ?? res);
      if (!ch?.id) throw new Error(res?.data?.message || 'Chapter could not be created.');
      setChapters((p) => [...p, {
        id: ch.id, title: ch.title, topics: [], expanded: true,
        relatedChapterId: ch.relatedChapterId || links,
        relatedChapters: [],
      }]);
      setNewLinks([]);
      setPicker(null);
    } catch (err) {
      console.error('createChapter failed:', err);
      setNew(title);
    } finally {
      // Always released, so a failed create does not leave the button dead.
      setAdding(null);
    }
  };
  // Append the chapter(s) an import produced, skipping any already in the list.
  const importChapters = async (payload) => {
    const list = Array.isArray(payload) ? payload : [payload].filter(Boolean);
    const mapped = await Promise.all(list.map(async (ch) => {
      let topics = [];
      try {
        const tr = await adminServices.getChapterTopics(ch.id);
        topics = (tr?.data ?? []).map((t) => ({ id: t.id, title: t.title, rawMarkdown: t.rawMarkdown || '' }));
      } catch { /* topics also load lazily when a topic is opened */ }
      return {
        id: ch.id, title: ch.title, topics, expanded: true,
        relatedChapterId: ch.relatedChapterId || [],
        relatedChapters:  ch.relatedChapters  || [],
      };
    }));
    setChapters((p) => [...p, ...mapped.filter((m) => !p.some((c) => c.id === m.id))]);
  };

  // Chapter and topic deletes are irreversible and used to fire on a single
  // click. They now go through the shared DeleteAlert, which owns the in-flight
  // spinner itself and clears it when `open` changes.
  const confirmDelete = async () => {
    if (!pendingDel) return;
    const { kind, chId, topId } = pendingDel;
    try {
      if (kind === 'chapter') {
        await adminServices.deleteEbookChapter(chId);
        setChapters((p) => p.filter((c) => c.id !== chId));
      } else {
        await adminServices.deleteEbookTopic(topId);
        setChapters((p) => p.map((c) => (c.id === chId
          ? { ...c, topics: c.topics.filter((t) => t.id !== topId) }
          : c)));
      }
    } catch (err) {
      console.error(`delete ${kind} failed:`, err);
    } finally {
      // Always closes: the dialog's spinner would otherwise spin forever on a
      // failed request.
      setPendingDel(null);
    }
  };
  const saveEdit = async (chId) => {
    if (!editVal.trim()) { setEditing(null); return; }
    try {
      await adminServices.updateEbookChapter(chId, { title: editVal.trim() });
      setChapters((p) => p.map((c) => (c.id === chId ? { ...c, title: editVal.trim() } : c)));
    } catch (err) { console.error('updateChapter failed:', err); }
    setEditing(null);
  };
  const togCh  = (chId) => setChapters((p) => p.map((c) => (c.id === chId ? { ...c, expanded: !c.expanded } : c)));
  const addTop = async (cid) => {
    const v = (topicIn[cid] || '').trim();
    // Same double-submit as addCh: the box keeps its text until the request
    // returns, so repeated clicks each created another topic.
    if (!v || addingTopic) return;
    setAddingTopic(cid);
    setTopicIn((p) => ({ ...p, [cid]: '' }));
    try {
      const ch = chapters.find((c) => c.id === cid);
      const orderIndex = ch?.topics?.length ?? 0;
      const res  = await adminServices.createChapterTopic(cid, { title: v, topicNumber: orderIndex + 1, orderIndex, rawMarkdown: '' });
      const top  = res?.ok === false ? null : (res?.data ?? res);
      if (!top?.id) throw new Error(res?.data?.message || 'Topic could not be created.');
      setChapters((p) => p.map((c) => c.id === cid ? { ...c, topics: [...c.topics, { id: top.id, title: top.title, rawMarkdown: '' }] } : c));
    } catch (err) {
      console.error('createTopic failed:', err);
      setTopicIn((p) => ({ ...p, [cid]: v }));
    } finally {
      setAddingTopic(null);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 py-3 border-b border-slate-200 flex-shrink-0">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Book Structure</p>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-3 flex flex-col gap-2">
        {chapters.map((ch) => (
          <div key={ch.id} className="flex-shrink-0 rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="flex items-center gap-2 px-3 py-2.5 bg-gradient-to-r from-indigo-50 to-white">
              <button type="button" onClick={() => togCh(ch.id)} className="text-indigo-500 flex-shrink-0"><Ico d={ch.expanded ? IC.chevU : IC.chevD} size={14} /></button>
              {editingId === ch.id
                ? (
                  <input
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    className="flex-1 min-w-0 text-xs font-bold text-indigo-900 bg-white border border-indigo-300 rounded px-1.5 py-0.5 outline-none"
                    value={editVal}
                    onChange={(e) => setEditVal(e.target.value)}
                    onBlur={() => saveEdit(ch.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(ch.id); if (e.key === 'Escape') setEditing(null); }}
                  />
                )
                : (
                  <button
                    type="button"
                    className="flex-1 text-xs font-bold text-indigo-900 truncate leading-snug text-left bg-transparent border-none p-0 cursor-pointer"
                    onDoubleClick={() => { setEditing(ch.id); setEditVal(ch.title); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setEditing(ch.id); setEditVal(ch.title); } }}
                    title="Double-click to rename"
                  >
                    {ch.title}
                  </button>
                )
              }
              <button
                type="button"
                onClick={() => setPicker(pickerFor === ch.id ? null : ch.id)}
                title="Link to assessment chapters"
                className={`flex-shrink-0 transition-colors ${(ch.relatedChapterId || []).length ? 'text-indigo-500 hover:text-indigo-700' : 'text-slate-300 hover:text-indigo-500'}`}
              >
                <Ico d={IC.link} size={13} />
              </button>
              <button type="button" onClick={() => setPendingDel({ kind: 'chapter', chId: ch.id, title: ch.title })} className="text-slate-300 hover:text-red-500 flex-shrink-0 transition-colors"><Ico d={IC.trash} size={13} /></button>
            </div>

            {/* Linked assessment chapters — the tag that lets a wrong answer find
                this chapter. Shown on the row itself so an unmapped chapter is
                obvious while authoring rather than discovered later. */}
            <div ref={(el) => { anchorRefs.current[ch.id] = el; }} className="relative px-3 pb-2 pt-0.5 bg-gradient-to-r from-indigo-50 to-white">
              {(ch.relatedChapterId || []).length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {linkNames(ch).map((name, i) => (
                    <span key={ch.relatedChapterId[i]} className="inline-flex items-center gap-1 bg-white border border-indigo-200 text-indigo-700 rounded-full px-2 py-0.5 text-[10px] max-w-full">
                      <Ico d={IC.exam} size={8} />
                      <span className="truncate" title={name}>{name}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        title="Unlink"
                        onClick={() => saveLinks(ch.id, ch.relatedChapterId.filter((x) => x !== ch.relatedChapterId[i]))}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') saveLinks(ch.id, ch.relatedChapterId.filter((x) => x !== ch.relatedChapterId[i])); }}
                        className="text-indigo-300 hover:text-red-500 cursor-pointer flex-shrink-0"
                      >
                        <Ico d={IC.x} size={8} />
                      </span>
                    </span>
                  ))}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setPicker(pickerFor === ch.id ? null : ch.id)}
                  className="text-[10px] text-slate-400 hover:text-indigo-600 bg-transparent border-none p-0 cursor-pointer"
                >
                  + Link assessment chapter
                </button>
              )}
              {linkError?.chId === ch.id && (
                <p className="mt-1 text-[10px] text-rose-600">{linkError.message}</p>
              )}
              {pickerFor === ch.id && (
                <AssessmentChapterPicker
                  anchorEl={anchorRefs.current[ch.id]}
                  options={assessmentChapters}
                  value={ch.relatedChapterId || []}
                  onChange={(ids) => saveLinks(ch.id, ids)}
                  onClose={() => setPicker(null)}
                />
              )}
            </div>

            {ch.expanded && (
              <div className="px-3 py-2 flex flex-col gap-1 bg-slate-50">
                {ch.topics.map((t) => {
                  const isActive = activeTopic?.topicId === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={`flex items-center gap-1.5 group py-1 px-1.5 rounded-lg cursor-pointer transition-colors w-full text-left ${isActive ? 'bg-indigo-100' : 'hover:bg-slate-100'}`}
                      onClick={() => onTopicSelect({ chapterId: ch.id, topicId: t.id, title: t.title, rawMarkdown: t.rawMarkdown || '' })}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isActive ? 'bg-indigo-500' : 'bg-indigo-300'}`} />
                      <span className={`flex-1 text-xs truncate ${isActive ? 'font-semibold text-indigo-800' : 'text-slate-600'}`}>{t.title}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setPendingDel({ kind: 'topic', chId: ch.id, topId: t.id, title: t.title }); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setPendingDel({ kind: 'topic', chId: ch.id, topId: t.id, title: t.title }); } }}
                        className="opacity-0 group-hover:opacity-100 text-red-400 transition-opacity flex-shrink-0"
                      >
                        <Ico d={IC.x} size={11} />
                      </span>
                    </button>
                  );
                })}
                <div className="flex gap-1 mt-1.5">
                  <input
                    className="flex-1 min-w-0 text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-400 bg-white"
                    placeholder="Add topic..."
                    value={topicIn[ch.id] || ''}
                    onChange={(e) => setTopicIn((p) => ({ ...p, [ch.id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') addTop(ch.id); }}
                  />
                  <button type="button" onClick={() => addTop(ch.id)} disabled={!!addingTopic || !(topicIn[ch.id] || '').trim()} className="flex-shrink-0 text-indigo-500 hover:text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed px-1">{addingTopic === ch.id ? <Spinner size={14} /> : <Ico d={IC.plus} size={15} />}</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {/* The chapter being created, shown where it will land. Without it the
            title simply vanished from the box with nothing to replace it. */}
        {adding && (
          <div className="flex-shrink-0 rounded-xl border border-dashed border-indigo-300 bg-indigo-50/60 px-3 py-2.5 flex items-center gap-2">
            <Spinner />
            <span className="flex-1 text-xs font-bold text-indigo-900 truncate" title={adding}>{adding}</span>
            <span className="text-[10px] font-semibold text-indigo-400 flex-shrink-0">Adding…</span>
          </div>
        )}
      </div>
      <div ref={(el) => { anchorRefs.current.new = el; }} className="p-3 border-t border-slate-200 flex-shrink-0 relative">
        {/* eBooks -> Content creator "View": the Import Chapter button was clipped off the 256px sidebar, because an input will not shrink below its default intrinsic width unless min-w-0 lets it. */}
        <div className="flex gap-2">
          <input
            className="flex-1 min-w-0 text-xs border border-dashed border-indigo-300 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-500 bg-indigo-50 placeholder-indigo-300"
            placeholder="New chapter title..."
            value={newTitle}
            onChange={(e) => setNew(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCh(); }}
          />
          <button type="button" onClick={addCh} disabled={!!adding || !newTitle.trim()} title="Add chapter" className="flex-shrink-0 bg-indigo-600 text-white rounded-xl px-3 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">{adding ? <Spinner size={16} cls="border-indigo-300 border-t-white" /> : <Ico d={IC.plus} size={16} />}</button>
          <button type="button" onClick={() => setImporting(true)} title="Import chapter from a Word document" className="flex-shrink-0 border border-indigo-300 text-indigo-600 rounded-xl px-3 hover:bg-indigo-50 transition-colors"><Ico d={IC.upload} size={16} /></button>
        </div>
        {/* Tag the chapter as it's created, so mapping is part of authoring
            rather than a second pass someone has to remember to make. */}
        <button
          type="button"
          onClick={() => setPicker(pickerFor === 'new' ? null : 'new')}
          className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] text-slate-500 hover:text-indigo-600 bg-transparent border-none p-0 cursor-pointer"
        >
          <Ico d={IC.link} size={10} />
          {newLinks.length
            ? `${newLinks.length} assessment chapter${newLinks.length > 1 ? 's' : ''} linked`
            : 'Link assessment chapters (optional)'}
        </button>
        {pickerFor === 'new' && (
          <AssessmentChapterPicker
            anchorEl={anchorRefs.current.new}
            options={assessmentChapters}
            value={newLinks}
            onChange={setNewLinks}
            onClose={() => setPicker(null)}
          />
        )}
      </div>
      {importing && (
        <ChapterImportModal
          ebookId={ebookId}
          onClose={() => setImporting(false)}
          onImported={importChapters}
        />
      )}
      {pendingDel && (
        <DeleteAlert
          open={!!pendingDel}
          close={() => setPendingDel(null)}
          confirm={confirmDelete}
          title={pendingDel.kind === 'chapter' ? 'Delete chapter' : 'Delete topic'}
          subTitle={pendingDel.kind === 'chapter'
            ? `“${pendingDel.title || 'Untitled chapter'}” and its topics will be removed from this eBook. This cannot be undone.`
            : `“${pendingDel.title || 'Untitled topic'}” will be removed from this eBook. This cannot be undone.`}
        />
      )}
    </div>
  );
}
ChapterSidebar.propTypes = {
  chapters:      PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), title: PropTypes.string, topics: PropTypes.array, expanded: PropTypes.bool, relatedChapterId: PropTypes.array })).isRequired,
  setChapters:   PropTypes.func.isRequired,
  ebookId:       PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  activeTopic:   PropTypes.shape({ topicId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  onTopicSelect: PropTypes.func.isRequired,
  assessmentChapters: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, chapterName: PropTypes.string })),
};
ChapterSidebar.defaultProps = { activeTopic: null, assessmentChapters: [] };

// ─── Publish Confirm ───────────────────────────────────────────────────────────
// Shown before anything that would abandon edits — the Back arrow, and moving to
// another topic. `discardLabel` differs between the two: leaving the page really
// does drop the edits, while switching topics only leaves them unsaved.
function UnsavedChangesDialog({ message, discardLabel, saving, onSave, onDiscard, onCancel }) {
  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center shrink-0"><Ico d={IC.warning} size={22} cls="text-amber-600" /></div>
          <div className="min-w-0">
            <h3 className="text-base font-black text-slate-900">Unsaved changes</h3>
            <p className="text-xs text-slate-500">{message}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={onDiscard} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-slate-300 text-rose-600 hover:bg-rose-50 disabled:opacity-50 transition-colors">
            {discardLabel}
          </button>
          <button type="button" onClick={onSave} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        <button type="button" onClick={onCancel} disabled={saving} className="w-full mt-3 py-2 text-xs font-semibold text-slate-500 hover:text-slate-800 bg-transparent border-none cursor-pointer disabled:opacity-50">
          Keep editing
        </button>
      </div>
    </div>
  );
}
UnsavedChangesDialog.propTypes = {
  message:      PropTypes.string.isRequired,
  discardLabel: PropTypes.string.isRequired,
  saving:       PropTypes.bool,
  onSave:       PropTypes.func.isRequired,
  onDiscard:    PropTypes.func.isRequired,
  onCancel:     PropTypes.func.isRequired,
};
UnsavedChangesDialog.defaultProps = { saving: false };

function PublishConfirm({ metadata, onConfirm, onClose, onFixDetails }) {
  const missing = [!metadata.title && 'Title', !metadata.subject && 'Subject', !metadata.classLevel && 'Class Level'].filter(Boolean);
  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center"><Ico d={IC.send} size={22} cls="text-indigo-600" /></div>
          <div><h3 className="text-base font-black text-slate-900">Publish eBook?</h3><p className="text-xs text-slate-500">{`"${metadata.title || 'Untitled'}" will be visible to students.`}</p></div>
        </div>
        {missing.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl mb-4 text-xs text-amber-800">
            <Ico d={IC.warning} size={14} cls="flex-shrink-0 mt-0.5" />
            <span>{`${missing.join(', ')} ${missing.length > 1 ? 'are' : 'is'} required before this eBook can go live.`}</span>
          </div>
        )}
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
          {/* Publishing with these blank put a half-described book in the student
              library, so the action becomes "go and fill them in" instead. */}
          {missing.length > 0 ? (
            <button type="button" onClick={onFixDetails} className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-amber-500 text-white hover:bg-amber-600 transition-colors">Add details</button>
          ) : (
            <button type="button" onClick={onConfirm} className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">Publish</button>
          )}
        </div>
      </div>
    </div>
  );
}
PublishConfirm.propTypes = {
  metadata:  PropTypes.shape({ title: PropTypes.string, subject: PropTypes.string, classLevel: PropTypes.string }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose:   PropTypes.func.isRequired,
  onFixDetails: PropTypes.func.isRequired,
};

// ─── Main EBookCreator ─────────────────────────────────────────────────────────
export default function EBookCreator() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [mode, setMode]               = useState('edit');
  const [markdown, setMarkdown]       = useState('');
  const [chapters, setChapters]       = useState([]);
  // The assessment chapters an eBook chapter can be tagged to. Loaded once and
  // handed to every picker — the list is the same for the whole book.
  const [assessmentChapters, setAssessmentChapters] = useState([]);
  // Book-level tags (ebooks.related_chapter_id), edited from the Metadata panel.
  // Distinct from the per-chapter tags held on each row in `chapters`.
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [activeTopic, setActiveTopic] = useState(null);
  const [savedStatus, setSaved]       = useState('saved');   
  const [published, setPublished]     = useState(false);
  const [showMeta, setShowMeta]       = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [showLeave, setShowLeave]     = useState(false);

  // Reads the status the page already tracks: every edit path (handleMarkdown,
  // handleMeta, media changes) sets it to 'unsaved'.
  const handleBack = () => {
    if (savedStatus === 'unsaved') { setShowLeave(true); return; }
    navigate('/app/ebook-list');
  };
  const leaveAfterSave = async () => {
    const ok = await handleSave();
    if (ok) { setShowLeave(false); navigate('/app/ebook-list'); }
  };

  const [panel, setPanel]             = useState(null);
  const [sideOpen, setSideOpen]       = useState(true);
  const [loadingData, setLoadingData] = useState(true);
  const [mediaError, setMediaError]   = useState('');

  const [metadata, setMetadata] = useState({
    title: '', subject: '', classLevel: '', language: 'English', board: '', description: '', shortDescription: '', examTypeId: '',
  });
  const [coverImage, setCoverImage] = useState(null);
  const [coverImgPreview, setCoverImgPreview] = useState(null);
  const [selectedEbooks, setSelectedEbooks] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedAssessments, setSelectedAssessments] = useState([]);

  const textareaRef    = useRef(null);
  const pendingUploads = useRef(new Map());

  useEffect(() => {
    if (!id) { navigate('/app/ebook-list', { replace: true }); return; }
    (async () => {
      try {
        const res  = await adminServices.getEbookById(id);
        const data = res?.data ?? res;
        if (data) {
          setMetadata({
            title:       data.title       || '',
            subject:     data.subjectId   || '',
            classLevel:  data.classLevel  || '',
            language:    data.language    || 'English',
            board:       data.board       || '',
            description: data.description || '',
            shortDescription: data.shortDescription || '',
            examTypeId: data.examTypeId != null ? String(data.examTypeId) : '',
            coverImageUrl: data.coverImageUrl || null,
            relatedContent: data.relatedContent || { courses: [], assessments: [], ebooks: [] },
          });
          setPublished(Boolean(data.isPublished));
          if (data.relatedContent) {
            setSelectedCourses((data.relatedContent.courses || []).map(c => c.id));
            setSelectedAssessments((data.relatedContent.assessments || []).map(a => a.id));
            setSelectedEbooks((data.relatedContent.ebooks || []).map(e => e.id));
          }
          setSelectedChapters(Array.isArray(data.relatedChapterId) ? data.relatedChapterId : []);
          if (data.coverImageUrl) setCoverImgPreview(data.coverImageUrl);
          try {
            const chapRes    = await adminServices.getEbookChapters(id);
            const chapList   = Array.isArray(chapRes?.data) ? chapRes.data : [];
            const withTopics = await Promise.all(chapList.map(async (ch) => {
              try {
                const topRes = await adminServices.getChapterTopics(ch.id);
                const topics = (topRes?.data ?? []).map((t) => ({ id: t.id, title: t.title, rawMarkdown: t.rawMarkdown || '' }));
                return {
                  id: ch.id, title: ch.title, topics, expanded: false,
                  relatedChapterId: ch.relatedChapterId || [],
                  relatedChapters:  ch.relatedChapters  || [],
                };
              } catch {
                return {
                  id: ch.id, title: ch.title, topics: [], expanded: false,
                  relatedChapterId: ch.relatedChapterId || [],
                  relatedChapters:  ch.relatedChapters  || [],
                };
              }
            }));
            setChapters(withTopics);
          } catch { setChapters([]); }
        }
      } catch (err) { console.error('Failed to load ebook:', err); }
      finally { setLoadingData(false); }
    })();
  }, [id, navigate]);

  // Chapter-tagging vocabulary. Deliberately independent of the eBook load: a
  // failure here costs the author the picker, not the editor, so it must not take
  // the page down with it.
  useEffect(() => {
    (async () => {
      try {
        const res = await adminServices.getAssessmentChaptersForEbook('', 200);
        setAssessmentChapters(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to load assessment chapters:', err);
        setAssessmentChapters([]);
      }
    })();
  }, []);

  const handleMeta = (key, val) => { setMetadata((p) => ({ ...p, [key]: val })); setSaved('unsaved'); };

  // The Metadata dialog also changes the cover image, the assessment-chapter
  // tags and the related content — through raw setters that bypass handleMeta,
  // so none of them marked the book dirty. Wrapped only where the dialog gets
  // them: the initial load calls the same setters directly and must stay clean.
  const markDirty = (fn) => (...args) => { fn(...args); setSaved('unsaved'); };
  const handleTopicSelect = (topic) => {
    setActiveTopic(topic);
    setMarkdown(topic.rawMarkdown || '');
   
    if (!topic.rawMarkdown) {
      adminServices.getTopicById(topic.topicId).then((res) => {
        const full = res?.data ?? res;
        const md   = full?.rawMarkdown || '';
        setMarkdown(md);
        setChapters((prev) => prev.map((ch) =>
          ch.id === topic.chapterId
            ? { ...ch, topics: ch.topics.map((t) => (t.id === topic.topicId ? { ...t, rawMarkdown: md } : t)) }
            : ch
        ));
      }).catch(() => {});
    }
  };

  const handleMarkdown = (val) => {
    setMarkdown(val);
    setSaved('unsaved');
    if (activeTopic) {
      setChapters((prev) => prev.map((ch) =>
        ch.id === activeTopic.chapterId
          ? { ...ch, topics: ch.topics.map((t) => (t.id === activeTopic.topicId ? { ...t, rawMarkdown: val } : t)) }
          : ch
      ));
    }
  };

  // One payload builder for every book-level write. Sent as FormData because the
  // API only accepts the cover image that way — the header Save button used to
  // send a plain object, so the image was silently dropped and relatedChapterId
  // was never included at all.
  const buildEbookFormData = (classLevelFallback) => {
    const fd = new FormData();
    fd.append('title', metadata.title || 'Untitled');
    if (metadata.subject) fd.append('subjectId', metadata.subject);
    fd.append('classLevel', metadata.classLevel || classLevelFallback || '');
    if (metadata.board) fd.append('board', metadata.board);
    fd.append('language', metadata.language || 'English');
    if (metadata.description) fd.append('description', metadata.description);
    if (metadata.shortDescription) fd.append('shortDescription', metadata.shortDescription);
    fd.append('relatedContent', JSON.stringify({
      courses:     selectedCourses.map((cid) => ({ id: cid })),
      assessments: selectedAssessments.map((aid) => ({ id: aid })),
      ebooks:      selectedEbooks.map((eid) => ({ id: eid })),
    }));
    fd.append('relatedChapterId', JSON.stringify(selectedChapters || []));
    fd.append('examTypeId', metadata.examTypeId || '');
    if (coverImage instanceof File) fd.append('coverImage', coverImage);
    return fd;
  };

  // `classLevelFallback` exists only for Publish, which defaults a blank class
  // level to 'General'. Callers must invoke it with no argument — never pass it
  // straight to onClick, or React's click event lands in that slot.
  const handleSave = async (classLevelFallback) => {
    setSaved('saving');
    try {
      let mdToSave = markdown;
      if (pendingUploads.current.size > 0) {
        const uploads = await Promise.all([...pendingUploads.current.entries()].map(async ([blob, file]) => {
          try {
            const fd = new FormData();
            fd.append('file', file);
            // The eBook media endpoint rather than the course image one: this
            // accepts video as well, which drag-and-drop now allows.
            const res = await adminServices.uploadEbookMedia(fd);
        
            const realUrl = res?.ok ? (res?.data?.url || res?.data?.imageUrl) : null;
            if (!realUrl) {
              console.error('Media upload failed', res?.status, res?.problem, res?.data);
              return { blob, name: file.name, ok: false };
            }
            return { blob, realUrl, ok: true };
          } catch (e) {
            console.error('Media upload failed', e);
            return { blob, name: file.name, ok: false };
          }
        }));

        uploads.filter((u) => u.ok).forEach((u) => {
          mdToSave = mdToSave.split(u.blob).join(u.realUrl);
          pendingUploads.current.delete(u.blob);
          URL.revokeObjectURL(u.blob);
        });

        const failed = uploads.filter((u) => !u.ok);
        if (failed.length) {
          
          setMediaError(`Could not upload ${failed.map((f) => `“${f.name}”`).join(', ')}. Nothing was saved — please try again.`);
          setSaved('unsaved');
          return false;
        }

        if (mdToSave !== markdown) {
          setMarkdown(mdToSave);
          setChapters((prev) => prev.map((ch) =>
            ch.id === activeTopic?.chapterId
              ? { ...ch, topics: ch.topics.map((t) => (t.id === activeTopic?.topicId ? { ...t, rawMarkdown: mdToSave } : t)) }
              : ch
          ));
        }
      }
  
      if (mdToSave.includes('blob:')) {
        setMediaError('An image has not finished uploading, so nothing was saved. Please try saving again.');
        setSaved('unsaved');
        return false;
      }
      await adminServices.updateEbook(id, buildEbookFormData(classLevelFallback));
      if (activeTopic?.topicId) {
        await adminServices.updateEbookTopic(activeTopic.topicId, { rawMarkdown: mdToSave });
      } else if (mdToSave.trim()) {
    
        setMediaError('Nothing was saved — no topic is selected, so there is nowhere to store this content. Pick a topic in the left sidebar and save again.');
        setSaved('unsaved');
        return false;
      }
      setSaved('saved');
      // Reported so "Save & leave" can keep the author on the page when the
      // save failed, rather than navigating away and losing the edits.
      return true;
    } catch (err) { console.error('Save failed:', err); setSaved('unsaved'); return false; }
  };

  const handlePublish = async () => {
    setShowPublish(false);
    // Publish makes what is on screen visible to students, so anything unsaved
    // has to reach the server first. This used to push the metadata only, which
    // left the live book showing the previous content while the UI said "saved".
    if (savedStatus === 'unsaved') {
      const ok = await handleSave('General');
      // A failed save must not go live: the flag would flip over stale content.
      if (!ok) return;
    }
    setSaved('saving');
    try {
      await adminServices.publishEbook(id, true);
      setPublished(true);
      setSaved('saved');
    } catch (err) { console.error('Publish failed:', err); setSaved('unsaved'); }
  };

  // Width, alignment and alt only. No src rewrite and no removal, so a placed
  // picture is never orphaned in the bucket and nothing queued for upload can
  // stop being referenced.
  const handleMediaChange = useCallback((descriptor, changes) => {
    if (!descriptor || !changes) return;
    const next = applyMediaChanges(markdown, descriptor, changes);
    if (next === markdown) return;
    handleMarkdown(next);
  }, [markdown, handleMarkdown]);

  // Shared by the editor pane and the preview pane. Media is inserted immediately
  // against a local blob URL and swapped for the real one on save, so the author
  const insertMediaFiles = useCallback((fileList, insertPos) => {
    const dropped = Array.from(fileList || []);
    if (!dropped.length) return;

    if (!activeTopic) {
      setMediaError('Select a topic in the left sidebar first — images are added to the topic you are editing.');
      return;
    }

    // Partitioned in one pass — the first cut classified every file twice.
    const usable   = [];
    const rejected = [];
    dropped.forEach((file) => {
      const kind = mediaKindOfFile(file);
      if (kind) usable.push({ file, kind });
      else rejected.push(file);
    });

    if (!usable.length) {
      setMediaError(
        rejected.length === 1
          ? `“${rejected[0].name}” is not an image or video, so it was not added.`
          : 'None of those files are images or video, so nothing was added.'
      );
      return;
    }
    if (rejected.length) {
      setMediaError(`Added ${usable.length} file(s). Skipped ${rejected.map((f) => `“${f.name}”`).join(', ')} — not an image or video.`);
    } else {
      setMediaError('');
    }

    const snippet = usable.map(({ file, kind }) => {
      const blobUrl = URL.createObjectURL(file);
      pendingUploads.current.set(blobUrl, file);
      return kind === 'video'
        ? videoFigure(blobUrl)
        : mediaFigure(blobUrl, file.name.replace(/\.[^.]+$/, ''));
    }).join('');

    const pos = typeof insertPos === 'number' ? insertPos : markdown.length;
    handleMarkdown(`${markdown.slice(0, pos)}${snippet}${markdown.slice(pos)}`);
    const el = textareaRef.current;
    if (el) {
      setTimeout(() => { el.focus(); el.setSelectionRange(pos + snippet.length, pos + snippet.length); }, 0);
    }
  }, [markdown, handleMarkdown, activeTopic]);

  const handleTextareaDragOver = useCallback((e) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }, []);
  const handleTextareaDrop     = useCallback((e) => {
    if (!e.dataTransfer.files?.length) return;
    e.preventDefault();
    const el = textareaRef.current;
    const atCaret = el && document.activeElement === el;
    insertMediaFiles(e.dataTransfer.files, atCaret ? el.selectionStart : undefined);
  }, [insertMediaFiles]);

  const insertAtCursor = (snippet) => {

    if (!activeTopic) {
      setMediaError('Select a topic in the left sidebar first — content is added to the topic you are editing.');
      setPanel(null);
      return;
    }
    const el = textareaRef.current;
    if (!el) { handleMarkdown(markdown + snippet); setPanel(null); return; }
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const nv    = `${markdown.slice(0, start)}${snippet}${markdown.slice(end)}`;
    handleMarkdown(nv);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + snippet.length, start + snippet.length); }, 0);
    setPanel(null);
  };

  const wordCount = markdown.trim().split(/\s+/).filter(Boolean).length;

  const renderSaveBadge = () => {
    if (savedStatus === 'saving') return <span className="text-xs text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">Saving…</span>;
    if (savedStatus === 'saved')  return <span className="text-xs text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full">Saved ✓</span>;
    return <span className="text-xs text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">Unsaved</span>;
  };

  const MODE_BTNS = [
    { key: 'edit',    icon: IC.edit,  label: 'Edit'    },
    { key: 'split',   icon: IC.split, label: 'Split'   },
    { key: 'preview', icon: IC.eye,   label: 'Preview' },
  ];

  return (
    <Page title="Edit eBook" style={{ padding: 0 }}>
      {loadingData ? (
        <div className="flex items-center justify-center h-64">
          <span className="text-slate-400 text-sm">Loading eBook…</span>
        </div>
      ) : (
        // h-screen + box-border + the navbar-height top padding keeps the whole
        // editor inside the viewport: the app navbar sits in the padding, and
        // everything below (header, sidebar, panes) shares the remaining height.
        // Only the inner panes scroll — the header and the sidebar's own header
        // and footer stay pinned.
        <div className="flex flex-col h-screen box-border pt-16 lg:pt-[68px] bg-slate-100 font-sans overflow-hidden">
          <header className="flex items-center gap-2 px-4 py-0 bg-white border-b border-slate-200 shadow-sm z-10 flex-shrink-0" style={{ minHeight: 54 }}>
            <button type="button" onClick={handleBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors px-2 py-1.5 rounded-lg hover:bg-slate-100">
              <Ico d={IC.back} size={15} /><span className="hidden sm:inline text-sm font-medium">Back</span>
            </button>
            <div className="w-px h-5 bg-slate-200" />
            <button type="button" onClick={() => setSideOpen((p) => !p)} className="text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-indigo-50 transition-colors" title="Toggle sidebar">
              <Ico d={sideOpen ? IC.chevL : IC.chevR} size={15} />
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Ico d={IC.book} size={16} cls="text-indigo-500 flex-shrink-0" />
              <input
                id="ebook-header-title"
                type="text"
                aria-label="eBook title"
                className="text-sm font-bold text-slate-800 bg-transparent border-none outline-none w-full truncate placeholder-slate-400"
                placeholder="Untitled eBook…"
                value={metadata.title}
                onChange={(e) => handleMeta('title', e.target.value)}
              />
            </div>
            <span className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${published ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${published ? 'bg-emerald-500' : 'bg-slate-400'}`} />
              {published ? 'Published' : 'Draft'}
            </span>
            <div className="flex items-center bg-slate-100 rounded-xl p-1 gap-0.5">
              {MODE_BTNS.map(({ key, icon, label }) => (
                <button key={key} type="button" title={label} onClick={() => setMode(key)} className={['flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all', mode === key ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'].join(' ')}>
                  <Ico d={icon} size={13} /><span className="hidden md:inline">{label}</span>
                </button>
              ))}
            </div>
            {renderSaveBadge()}
            <button type="button" onClick={() => setShowMeta(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-slate-300 text-slate-600 bg-white hover:bg-slate-50 hover:border-indigo-400 hover:text-indigo-700 transition-colors">
              <Ico d={IC.info} size={13} /><span className="hidden sm:inline">Metadata</span>
            </button>
            <button type="button" onClick={() => handleSave()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 transition-colors">
              <Ico d={IC.save} size={13} /><span className="hidden sm:inline">Save</span>
            </button>
            <button type="button" onClick={() => setShowPublish(true)} className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm">
              <Ico d={IC.send} size={13} /><span className="hidden sm:inline">Publish</span>
            </button>
          </header>

          {mediaError && (
            <div className="flex items-center gap-2 px-4 py-2 bg-rose-50 border-b border-rose-200 text-rose-800 text-xs font-semibold flex-shrink-0">
              <Ico d={IC.info} size={14} />
              <span>{mediaError}</span>
              <button type="button" onClick={() => setMediaError('')} className="ml-auto text-rose-400 hover:text-rose-700">
                <Ico d={IC.x} size={14} />
              </button>
            </div>
          )}
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {sideOpen && (
              <aside className="w-64 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col overflow-hidden">
                <ChapterSidebar chapters={chapters} setChapters={setChapters} ebookId={id} activeTopic={activeTopic} onTopicSelect={handleTopicSelect} assessmentChapters={assessmentChapters} />
              </aside>
            )}
            <main className="flex-1 flex min-w-0 overflow-hidden bg-slate-100">
              {(mode === 'edit' || mode === 'split') && (
                <div className="flex flex-col min-w-0 overflow-hidden bg-white" style={{ width: mode === 'split' ? '50%' : '100%' }}>
                  <Toolbar textareaRef={textareaRef} value={markdown} onChange={handleMarkdown} onInsertSpecial={setPanel} />
                  {!activeTopic && (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 bg-slate-50 text-slate-400">
                      <Ico d={IC.book} size={36} cls="text-slate-300" />
                      <p className="text-sm font-medium">Select a topic from the sidebar to start editing</p>
                      <p className="text-xs">Create a chapter → add a topic → click to open</p>
                    </div>
                  )}
                  {activeTopic && (
                    <>
                      <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 flex-shrink-0">
                        <Ico d={IC.edit} size={13} cls="text-slate-400" />
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{activeTopic.title}</span>
                        <span className="ml-auto text-xs text-slate-400">Ctrl+S · Tab to indent</span>
                        <span className="text-xs text-slate-400">{wordCount} words</span>
                      </div>
                      <textarea
                        ref={textareaRef}
                        className="flex-1 w-full px-8 py-6 font-mono text-sm text-slate-800 bg-white resize-none focus:outline-none leading-loose placeholder-slate-300"
                        style={{ fontFamily: "'Fira Code','Cascadia Code',monospace", fontSize: '13.5px' }}
                        value={markdown}
                        onChange={(e) => handleMarkdown(e.target.value)}
                        placeholder="Start writing your eBook content in Markdown…"
                        spellCheck={false}
                        onDragOver={handleTextareaDragOver}
                        onDrop={handleTextareaDrop}
                        onKeyDown={(e) => {
                          if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); handleSave(); }
                          if (e.key === 'Tab') {
                            e.preventDefault();
                            const s  = e.target.selectionStart;
                            const nd = e.target.selectionEnd;
                            const nv = `${markdown.slice(0, s)}  ${markdown.slice(nd)}`;
                            handleMarkdown(nv);
                            setTimeout(() => { textareaRef.current.setSelectionRange(s + 2, s + 2); }, 0);
                          }
                        }}
                      />
                    </>
                  )}
                </div>
              )}
              {mode === 'split' && <div className="w-1 bg-slate-200 flex-shrink-0" />}
              {(mode === 'preview' || mode === 'split') && (
                <div className="flex flex-col min-w-0 overflow-hidden bg-white" style={{ width: mode === 'split' ? '50%' : '100%' }}>
                  <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 flex-shrink-0">
                    <Ico d={IC.eye} size={13} cls="text-slate-400" />
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Student Preview</span>
                    {metadata.subject && (
                      <span className="ml-auto text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold">{`${metadata.subject} · ${metadata.classLevel}`}</span>
                    )}
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <MarkdownPreview
                      content={markdown}
                      bookMeta={metadata}
                      onMediaChange={handleMediaChange}
                      onDropMedia={(files) => insertMediaFiles(files)}
                    />
                  </div>
                </div>
              )}
            </main>
          </div>

          {showMeta && (
            <MetadataModal
              metadata={metadata}
              onChange={handleMeta}
              onClose={() => setShowMeta(false)}
              coverImage={coverImage}
              coverImgPreview={coverImgPreview}
              setCoverImage={markDirty(setCoverImage)}
              setCoverImgPreview={setCoverImgPreview}
              selectedEbooks={selectedEbooks}
              selectedCourses={selectedCourses}
              selectedAssessments={selectedAssessments}
              setSelectedEbooks={markDirty(setSelectedEbooks)}
              setSelectedCourses={markDirty(setSelectedCourses)}
              setSelectedAssessments={markDirty(setSelectedAssessments)}
              assessmentChapters={assessmentChapters}
              selectedChapters={selectedChapters}
              setSelectedChapters={markDirty(setSelectedChapters)}
              ebookId={id} 
            />
          )}
          {showPublish && (
            <PublishConfirm
              metadata={metadata}
              onConfirm={handlePublish}
              onClose={() => setShowPublish(false)}
              onFixDetails={() => { setShowPublish(false); setShowMeta(true); }}
            />
          )}
          {showLeave && (
            <UnsavedChangesDialog
              message="Leave this eBook without saving? Your latest edits will be lost."
              discardLabel="Discard"
              saving={savedStatus === 'saving'}
              onSave={leaveAfterSave}
              onDiscard={() => { setShowLeave(false); navigate('/app/ebook-list'); }}
              onCancel={() => setShowLeave(false)}
            />
          )}
          {panel === 'image'     && <ImagePanel     onInsert={insertAtCursor} onClose={() => setPanel(null)} onPendingFile={(blob, file) => pendingUploads.current.set(blob, file)} />}
          {panel === 'video'     && <VideoPanel     onInsert={insertAtCursor} onClose={() => setPanel(null)} />}
          {panel === 'button'    && <ButtonPanel    onInsert={insertAtCursor} onClose={() => setPanel(null)} />}
          {panel === 'lifecycle' && <LifecyclePanel onInsert={insertAtCursor} onClose={() => setPanel(null)} onPendingFile={(blob, file) => pendingUploads.current.set(blob, file)} />}
        </div>
      )}
    </Page>
  );
}